/* Store Locator (BOPIS)
 *
 * Globals: WSSStore, openStoreLocator, openStoreLocatorFromCart, hydrateCartPickup,
 * closeStoreLocator, event 'wss:store-changed'.
 *
 * Data: Storefront locations + metafields, variant storeAvailability (PDP only),
 * Google Maps (or Photon / zippopotam.us for ZIP).
 *
 * Debug: __WSS_STORE_LOCATOR_DEBUG_INFO(true|false), ?store_locator_debug=1,
 * or localStorage wss_store_locator_debug=1.
 *
 * openStoreLocator(context): optional { sku, variantGid, fromPdp: true } shows per-store
 * stock on tiles (PDP pickup block, header on product pages, or cart pickup on PDP).
 *
 * First visit: may auto-pick nearest store via geolocation (theme setting, search radius + 5 mi GPS slack).
 */
(function () {
  'use strict';

  // ----- Config -----
  var STOREFRONT_API_VERSION = '2024-10';
  var MAPS_JS_BASE = 'https://maps.googleapis.com/maps/api/js';
  var ZIPGEO_URL = 'https://api.zippopotam.us/us/';
  var PHOTON_GEOCODE_URL = 'https://photon.komoot.io/api/';
  var COOKIE_NAME = 'wss_selected_store';
  var COOKIE_DAYS = 365;
  var LS_KEY = 'wssSelectedStore';
  var RADIUS_CAP_MILES_DEFAULT = 100;
  var RADIUS_CAP_MILES_MIN = 25;
  var RADIUS_CAP_MILES_MAX = 200;
  // Set from flyout data-radius-cap-miles in initRefs().
  var RADIUS_CAP_MILES = RADIUS_CAP_MILES_DEFAULT;
  // Extra radius for device GPS searches only.
  var GEO_RADIUS_SLACK_MILES = 5;
  var STORE_INFO_FALLBACK = '/pages/store-locator';
  var SS_GEO_AUTO_DENIED = 'wss_geo_auto_denied';
  var SS_GEO_AUTO_EMPTY = 'wss_geo_auto_no_nearby';
  var SS_PICKUP_PREF_STORE = 'wss_pickup_pref_store_id';
  var SS_CART_MODAL_CHECKOUT = 'wss_cart_modal_checkout';
  var CART_ATTR_FULFILLMENT = 'fulfillment_method';

  // Cart fulfillment toggle: 'pickup' | 'shipping' | null (not yet loaded / no explicit choice).
  // Pickup is only on when explicitly set to 'pickup' (see isCartPickupModeEnabled).
  var CART_FULFILLMENT_METHOD = null;
  // Set when the customer enables pickup with no saved store; resolved once a store is chosen.
  var pendingEnablePickup = false;
  // Cart pickup unavailable modal: skip intercept after modal checkout switches to shipping.
  var skipPickupModal = false;
  var pendingCheckoutSubmitter = null;
  var skipCheckoutSync = false;
  var cartPickupAvailabilityFetchId = 0;
  var hydrateCartPickupLinesTimer = null;
  var cartPickupAvailabilityByGid = {};

  // Set from flyout data attributes in initRefs().
  var SHOP_DOMAIN = '';
  var STOREFRONT_TOKEN = '';
  var GOOGLE_MAPS_API_KEY = '';
  var FLYOUT_ANIM_MS = 250;
  var locatorClosing = false;
  var locatorCloseCallbacks = [];

  // Debug: URL param, localStorage flag, or window.__WSS_STORE_LOCATOR_DEBUG.
  function dbgEnabled() {
    try {
      if (window.__WSS_STORE_LOCATOR_DEBUG === true) return true;
      if (window.localStorage && window.localStorage.getItem('wss_store_locator_debug') === '1') return true;
      return new URLSearchParams(window.location.search || '').get('store_locator_debug') === '1';
    } catch (error) {
      return false;
    }
  }
  function dbg() {
    if (!dbgEnabled()) return;
    var args = ['[StoreLocator]'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  // ----- Cookie helpers -----
  function readCookie(name) {
    var cookieMatch = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'));
    return cookieMatch ? decodeURIComponent(cookieMatch[1]) : null;
  }
  function writeCookie(name, value, days) {
    var expiryDate = new Date();
    expiryDate.setTime(expiryDate.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = name + '=' + encodeURIComponent(value) + ';expires=' + expiryDate.toUTCString() + ';path=/;samesite=lax';
  }
  function deleteCookie(name) {
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
  }

  function buildStoreCartAttributes(store) {
    return {
      selected_store_id: String(store.id || ''),
      selected_store_gid: store.gid || '',
      selected_store_name: store.name || '',
      selected_store_display_name: store.displayName || store.name || '',
      selected_store_zip: store.zip || '',
      selected_store_city_state: (store.city || '') + (store.state ? ', ' + store.state : '')
    };
  }

  // Saved store: cookie + localStorage + cart attributes + checkout pickup pref.
  var WSSStore = {
    get: function () {
      var raw = readCookie(COOKIE_NAME);
      if (!raw) {
        try {
          raw = window.localStorage.getItem(LS_KEY);
        } catch (error) {}
      }
      if (!raw) return null;
      try { return JSON.parse(raw); } catch (error) { return null; }
    },
    applyLocal: function (store, opts) {
      opts = opts || {};
      var previousStore = WSSStore.get();
      if (!store) return;
      var json = JSON.stringify(store);
      writeCookie(COOKIE_NAME, json, COOKIE_DAYS);
      try { window.localStorage.setItem(LS_KEY, json); } catch (error) {}
      window.dispatchEvent(new CustomEvent('wss:store-changed', {
        detail: {
          store: store,
          previousStore: previousStore,
          source: opts.source || 'local'
        }
      }));
      if (opts.skipCartPost) return Promise.resolve();
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: buildStoreCartAttributes(store) })
      }).catch(function () {});
    },
    set: function (store) {
      WSSStore.applyLocal(store, { skipCartPost: true });
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: buildStoreCartAttributes(store) })
      }).then(function () {
        // Best-effort background sync — do not block store apply UI on GraphQL.
        if (shouldForcePickupOnStoreSet()) {
          syncPickupPreference(store, { force: true });
        } else {
          // Shipping mode: keep shipping default but register this store as the
          // preferred pickup location for checkout.
          syncShippingPreference(store);
        }
      }).catch(function () {});
    },
    clear: function () {
      deleteCookie(COOKIE_NAME);
      try { window.localStorage.removeItem(LS_KEY); } catch (error) {}
      window.dispatchEvent(new CustomEvent('wss:store-changed', { detail: { store: null } }));
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: {
          selected_store_id: '',
          selected_store_gid: '',
          selected_store_name: '',
          selected_store_display_name: '',
          selected_store_zip: '',
          selected_store_city_state: ''
        } })
      }).then(function () {
        return clearPickupPreference();
      }).catch(function () {});
    }
  };
  window.WSSStore = WSSStore;

  // ----- DOM helpers -----
  function queryOne(selector, root) { return (root || document).querySelector(selector); }
  function queryAll(selector, root) { return Array.prototype.slice.call((root || document).querySelectorAll(selector)); }
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  // ----- Format helpers -----
  function stripCountryFromQueryLabel(label) {
    if (!label) return '';
    return String(label).trim()
      .replace(/,\s*(?:USA|U\.?S\.?A?\.?|United States(?:\s+of\s+America)?)\s*$/i, '')
      .trim();
  }
  function formatPhone(raw) {
    if (!raw) return '';
    var digits = String(raw).replace(/\D/g, '');
    if (digits.length === 11 && digits.charAt(0) === '1') digits = digits.slice(1);
    if (digits.length !== 10) return raw;
    return '(' + digits.slice(0, 3) + ')-' + digits.slice(3, 6) + '-' + digits.slice(6);
  }
  function formatTime(hhmm) {
    if (!hhmm || typeof hhmm !== 'string') return '';
    var parts = hhmm.split(':');
    var hours = parseInt(parts[0], 10);
    var minutes = parseInt(parts[1] || '0', 10);
    if (isNaN(hours)) return '';
    var suffix = hours >= 12 ? 'PM' : 'AM';
    var hour12 = hours % 12;
    if (hour12 === 0) hour12 = 12;
    var minsStr = (minutes < 10 ? '0' : '') + minutes;
    return hour12 + ':' + minsStr + suffix;
  }
  // store.hours metafield has a regular weekly schedule.
  function hasStoreHoursData(rawJson) {
    if (!rawJson) return false;
    try {
      var hoursObject = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
      return !!(hoursObject && hoursObject.regular);
    } catch (error) { return false; }
  }

  var HOURS_ALL_DAYS = [
    { key: 'mon', label: 'Monday' },
    { key: 'tue', label: 'Tuesday' },
    { key: 'wed', label: 'Wednesday' },
    { key: 'thu', label: 'Thursday' },
    { key: 'fri', label: 'Friday' },
    { key: 'sat', label: 'Saturday' },
    { key: 'sun', label: 'Sunday' }
  ];

  function dayHoursTime(regularHours, dayKey) {
    var dayRecord = regularHours[dayKey];
    if (!dayRecord || !dayRecord.open || !dayRecord.close) return '';
    return formatTime(dayRecord.open) + ' - ' + formatTime(dayRecord.close);
  }

  function hoursDisplayTime(timeStr) {
    return timeStr || 'Closed';
  }

  function buildHourRows(regularHours) {
    return HOURS_ALL_DAYS.map(function (day) {
      return {
        label: day.label,
        time: hoursDisplayTime(dayHoursTime(regularHours, day.key))
      };
    });
  }

  function formatHours(rawJson) {
    if (!rawJson) return null;
    try {
      var hoursObject = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
      var regularHours = hoursObject && hoursObject.regular;
      if (!regularHours) return null;
      return { rows: buildHourRows(regularHours) };
    } catch (error) { return null; }
  }

  var HOURS_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  function getTodayDayName() {
    return HOURS_DAY_NAMES[new Date().getDay()];
  }

  function markTodayHoursRows(rows) {
    if (!rows || !rows.length) return [];
    var todayName = getTodayDayName().toLowerCase();
    return rows.map(function (row) {
      return {
        label: row.label,
        time: row.time,
        isToday: String(row.label).toLowerCase() === todayName
      };
    });
  }

  function renderStoreTileHoursList(hoursEl, hours) {
    hoursEl.innerHTML = '';
    if (!hours || !hours.rows || !hours.rows.length) return;
    var displayRows = markTodayHoursRows(hours.rows);
    displayRows.forEach(function (row) {
      var li = document.createElement('li');
      if (row.isToday) li.classList.add('store-tile__hours-row--today');
      var label = document.createElement('span');
      label.className = 'store-tile__hours-label';
      label.textContent = row.label + ':';
      var time = document.createElement('span');
      time.className = 'store-tile__hours-time';
      time.textContent = row.time;
      li.appendChild(label);
      li.appendChild(time);
      hoursEl.appendChild(li);
    });
  }

  var METERS_PER_MILE = 1609.344;

  function distanceMiles(lat1, lng1, lat2, lng2) {
    if (window.google && google.maps && google.maps.geometry && google.maps.geometry.spherical) {
      var originLatLng = new google.maps.LatLng(lat1, lng1);
      var destLatLng = new google.maps.LatLng(lat2, lng2);
      return google.maps.geometry.spherical.computeDistanceBetween(originLatLng, destLatLng) / METERS_PER_MILE;
    }
    function toRadians(degrees) { return degrees * Math.PI / 180; }
    var earthRadiusMiles = 3958.8;
    var dLat = toRadians(lat2 - lat1);
    var dLng = toRadians(lng2 - lng1);
    var haversineA = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
    var centralAngle = 2 * Math.atan2(Math.sqrt(haversineA), Math.sqrt(1 - haversineA));
    return earthRadiusMiles * centralAngle;
  }

  // Never show 0.00 mi on tiles; floor at 0.01.
  function formatDistanceMilesDisplay(distance) {
    if (distance == null || isNaN(distance)) return '';
    var miles = Math.max(Number(distance), 0.01);
    return miles.toFixed(2) + ' Miles';
  }

  // First 5 digits of a US ZIP for matching.
  function normZip5(zipInput) {
    if (zipInput == null || zipInput === '') return null;
    var digits = String(zipInput).replace(/\D/g, '');
    if (digits.length < 5) return null;
    return digits.slice(0, 5);
  }

  // Numeric id from a Location GID string.
  function shopifyLocationNumericId(gidOrId) {
    if (gidOrId == null || gidOrId === '') return null;
    var gidString = String(gidOrId);
    var idMatch = gidString.match(/Location\/(\d+)/);
    if (idMatch) return idMatch[1];
    if (/^\d+$/.test(gidString)) return gidString;
    return null;
  }

  function storeAvailabilityLocationIdsMatch(apiLocId, selectedGidOrId) {
    if (apiLocId == null || selectedGidOrId == null) return false;
    if (apiLocId === selectedGidOrId) return true;
    var apiNumericId = shopifyLocationNumericId(apiLocId);
    var selectedNumericId = shopifyLocationNumericId(selectedGidOrId);
    return apiNumericId != null && selectedNumericId != null && apiNumericId === selectedNumericId;
  }

  function hasValidCoords(location) {
    return typeof location.lat === 'number' && typeof location.lng === 'number' &&
      !isNaN(location.lat) && !isNaN(location.lng) && isFinite(location.lat) && isFinite(location.lng);
  }

  function formatStoreGeocodeQuery(store) {
    return [store.address1, store.city, store.state, store.zip, store.country || 'US'].filter(Boolean).join(', ');
  }

  // Geocode stores missing lat/lng (updates objects in place).
  function enrichMissingCoordinates(locations) {
    if (!locations || !locations.length) return Promise.resolve(locations);
    var missing = locations.filter(function (location) { return !hasValidCoords(location); });
    if (!missing.length) return Promise.resolve(locations);
    if (dbgEnabled()) dbg('enrichMissingCoordinates: geocoding', missing.length, 'store(s) without lat/lng');
    return missing.reduce(function (chain, store) {
      return chain.then(function () {
        return geocodeTextWithFallbacks(formatStoreGeocodeQuery(store)).then(function (geocodeResult) {
          if (geocodeResult && typeof geocodeResult.lat === 'number' && typeof geocodeResult.lng === 'number') {
            store.lat = geocodeResult.lat;
            store.lng = geocodeResult.lng;
          }
        });
      });
    }, Promise.resolve()).then(function () { return locations; });
  }

  // [{ store, distance }] within maxRadiusMiles (default 100). ZIP match = 0 mi.
  function buildResultsWithDistance(locations, geo, searchZip5, opts) {
    opts = opts || {};
    var maxMi = typeof opts.maxRadiusMiles === 'number' ? opts.maxRadiusMiles : RADIUS_CAP_MILES;
    var out = [];
    var seen = {};
    locations.forEach(function (location) {
      var dist = null;
      var zipMatch = !!(searchZip5 && normZip5(location.zip) === searchZip5);
      if (zipMatch) dist = 0;
      else if (hasValidCoords(location) && geo) dist = distanceMiles(geo.lat, geo.lng, location.lat, location.lng);
      if (dist === null) return;
      if (dist > maxMi) return;
      if (seen[location.gid || location.id]) return;
      seen[location.gid || location.id] = true;
      out.push({ store: location, distance: dist });
    });
    out.sort(function (left, right) { return left.distance - right.distance; });
    return out;
  }

  function mergeDistanceResults(primary, secondary) {
    var byId = {};
    function add(list) {
      (list || []).forEach(function (item) {
        if (!item || !item.store) return;
        var key = String(item.store.gid || item.store.id);
        var prev = byId[key];
        if (!prev || item.distance < prev.distance) byId[key] = item;
      });
    }
    add(primary);
    add(secondary);
    var out = Object.keys(byId).map(function (storeKey) { return byId[storeKey]; });
    out.sort(function (left, right) { return left.distance - right.distance; });
    return out;
  }

  function debugExplainEmptyResults(locations, geo, searchZip5, opts) {
    if (!dbgEnabled() || !locations || !locations.length) return;
    opts = opts || {};
    var maxMi = typeof opts.maxRadiusMiles === 'number' ? opts.maxRadiusMiles : RADIUS_CAP_MILES;
    dbg('ZERO results — per-location diagnostic (coords / ZIP / ' + maxMi + 'mi):');
    locations.forEach(function (location, locationIndex) {
      var zipMatched = !!(searchZip5 && normZip5(location.zip) === searchZip5);
      var distanceMilesValue = hasValidCoords(location) && geo ? distanceMiles(geo.lat, geo.lng, location.lat, location.lng) : null;
      dbg('  [' + locationIndex + ']', {
        id: location.id,
        name: location.name,
        zip: location.zip,
        normZip: normZip5(location.zip),
        searchZip5: searchZip5,
        zipMatch: zipMatched,
        hasValidCoords: hasValidCoords(location),
        distMi: distanceMilesValue != null && isFinite(distanceMilesValue) ? Number(distanceMilesValue.toFixed(2)) : null,
        overRadiusCap: distanceMilesValue != null && distanceMilesValue > maxMi
      });
    });
  }

  // Fallback: match stores by ZIP when geocoding fails.
  function buildZipOnlyResults(locations, zip5) {
    var out = [];
    var seen = {};
    (locations || []).forEach(function (location) {
      if (normZip5(location.zip) !== zip5) return;
      if (seen[location.gid || location.id]) return;
      seen[location.gid || location.id] = true;
      out.push({ store: location, distance: 0 });
    });
    return out;
  }

  // ----- Geocoding -----
  var __mapsLoadPromise = null;

  function ensureGoogleMapsLoaded() {
    if (window.google && window.google.maps && window.google.maps.Geocoder) {
      return Promise.resolve();
    }
    if (!GOOGLE_MAPS_API_KEY) return Promise.reject(new Error('No Google Maps API key'));
    if (__mapsLoadPromise) return __mapsLoadPromise;
    __mapsLoadPromise = new Promise(function (resolve, reject) {
      var cbName = '__wssStoreLocatorMapsInit_' + Math.floor(Math.random() * 1e9);
      window[cbName] = function () {
        try { delete window[cbName]; } catch (error) { window[cbName] = undefined; }
        if (window.google && window.google.maps && window.google.maps.Geocoder) {
          if (dbgEnabled()) dbg('Google Maps JavaScript API ready');
          resolve();
        } else {
          __mapsLoadPromise = null;
          reject(new Error('google.maps not available after callback'));
        }
      };
      var scriptEl = document.createElement('script');
      scriptEl.async = true;
      scriptEl.defer = true;
      scriptEl.onerror = function () {
        __mapsLoadPromise = null;
        reject(new Error('Failed to load Google Maps JavaScript API'));
      };
      scriptEl.src = MAPS_JS_BASE +
        '?key=' + encodeURIComponent(GOOGLE_MAPS_API_KEY) +
        '&libraries=places,geometry&callback=' + cbName;
      document.head.appendChild(scriptEl);
    });
    return __mapsLoadPromise;
  }


  function geocodeViaMapsJs(query) {
    var trimmedQuery = (query || '').trim();
    if (!trimmedQuery) return Promise.resolve(null);
    return new Promise(function (resolve) {
      if (!(window.google && google.maps && google.maps.Geocoder)) {
        resolve(null);
        return;
      }
      var geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        { address: trimmedQuery, componentRestrictions: { country: 'us' } },
        function (results, status) {
          if (status !== 'OK' || !results || !results.length) {
            if (dbgEnabled()) dbg('Maps JS Geocoder status', status, 'for', trimmedQuery);
            resolve(null);
            return;
          }
          var location = results[0].geometry.location;
          var lat = typeof location.lat === 'function' ? location.lat() : location.lat;
          var lng = typeof location.lng === 'function' ? location.lng() : location.lng;
          resolve({
            lat: lat,
            lng: lng,
            label: results[0].formatted_address || trimmedQuery
          });
        }
      );
    });
  }

  function geocode(query) {
    var trimmedQuery = (query || '').trim();
    if (!trimmedQuery) return Promise.resolve(null);

    var zipMatch = trimmedQuery.match(/^(\d{5})$/);
    if (zipMatch) {
      return fetch(ZIPGEO_URL + zipMatch[1])
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (json) {
          if (dbgEnabled()) dbg('zippopotam.us', zipMatch[1], 'places:', json && json.places ? json.places.length : 0);
          if (json && json.places && json.places[0]) {
            var zipCoords = {
              lat: parseFloat(json.places[0].latitude),
              lng: parseFloat(json.places[0].longitude),
              label: zipMatch[1]
            };
            if (dbgEnabled()) dbg('geocode ZIP via zippopotam →', zipCoords);
            return zipCoords;
          }
          if (dbgEnabled()) dbg('zippopotam empty; Maps/Photon fallback for', trimmedQuery);
          return geocodeTextWithFallbacks(trimmedQuery);
        })
        .catch(function (err) {
          if (dbgEnabled()) dbg('zippopotam fetch failed', err);
          return geocodeTextWithFallbacks(trimmedQuery);
        });
    }
    return geocodeTextWithFallbacks(trimmedQuery);
  }

  function geocodePhoton(query) {
    var trimmedQuery = (query || '').trim();
    if (!trimmedQuery) return Promise.resolve(null);
    return fetch(PHOTON_GEOCODE_URL + '?q=' + encodeURIComponent(trimmedQuery) + '&limit=1&lang=en')
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (json) {
        if (!json || !json.features || !json.features.length) {
          if (dbgEnabled()) dbg('Photon geocode: no features for', trimmedQuery);
          return null;
        }
        var feature = json.features[0];
        var geom = feature.geometry;
        if (!geom || geom.type !== 'Point' || !geom.coordinates || geom.coordinates.length < 2) return null;
        var lng = parseFloat(geom.coordinates[0]);
        var lat = parseFloat(geom.coordinates[1]);
        if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) return null;
        var properties = feature.properties || {};
        var label = [properties.name, properties.city || properties.county, properties.state, properties.country].filter(Boolean).join(', ') || trimmedQuery;
        var out = { lat: lat, lng: lng, label: label };
        if (dbgEnabled()) dbg('Photon geocode →', out);
        return out;
      })
      .catch(function (err) {
        if (dbgEnabled()) dbg('Photon geocode failed', err);
        return null;
      });
  }

  function geocodeTextWithFallbacks(query) {
    if (!GOOGLE_MAPS_API_KEY) {
      if (dbgEnabled()) dbg('No Maps API key; Photon geocode for', query);
      return geocodePhoton(query);
    }
    return ensureGoogleMapsLoaded()
      .then(function () { return geocodeViaMapsJs(query); })
      .catch(function (err) {
        if (dbgEnabled()) dbg('Maps JS load/geocode failed', err);
        return null;
      })
      .then(function (geocodeResult) {
        if (geocodeResult) return geocodeResult;
        if (dbgEnabled()) dbg('Text geocode: Photon fallback for', query);
        return geocodePhoton(query);
      });
  }

  function zip5FromMapsAddressComponents(components) {
    if (!components || !components.length) return null;
    for (var componentIndex = 0; componentIndex < components.length; componentIndex++) {
      var component = components[componentIndex];
      if (!component || !component.types || component.types.indexOf('postal_code') === -1) continue;
      return normZip5(component.long_name || component.short_name);
    }
    return null;
  }

  function reverseGeocodeViaMapsJs(lat, lng) {
    return new Promise(function (resolve) {
      if (!(window.google && google.maps && google.maps.Geocoder)) {
        resolve(null);
        return;
      }
      var geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat: lat, lng: lng } }, function (results, status) {
        if (status !== 'OK' || !results || !results.length) {
          if (dbgEnabled()) dbg('Maps JS reverse geocode status', status);
          resolve(null);
          return;
        }
        var zip5 = null;
        for (var resultIndex = 0; resultIndex < results.length && !zip5; resultIndex++) {
          zip5 = zip5FromMapsAddressComponents(results[resultIndex].address_components);
        }
        if (dbgEnabled()) dbg('Maps JS reverse geocode zip5', zip5);
        resolve(zip5);
      });
    });
  }

  var PHOTON_REVERSE_URL = 'https://photon.komoot.io/reverse';

  function reverseGeocodePhoton(lat, lng) {
    return fetch(PHOTON_REVERSE_URL + '?lat=' + encodeURIComponent(lat) + '&lon=' + encodeURIComponent(lng) + '&lang=en')
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (json) {
        if (!json || !json.features || !json.features.length) {
          if (dbgEnabled()) dbg('Photon reverse: no features');
          return null;
        }
        var zip5 = null;
        for (var featureIndex = 0; featureIndex < json.features.length && !zip5; featureIndex++) {
          var properties = json.features[featureIndex].properties || {};
          zip5 = normZip5(properties.postcode);
        }
        if (dbgEnabled()) dbg('Photon reverse geocode zip5', zip5);
        return zip5;
      })
      .catch(function (err) {
        if (dbgEnabled()) dbg('Photon reverse geocode failed', err);
        return null;
      });
  }

  function reverseGeocodeToZip5(lat, lng) {
    if (typeof lat !== 'number' || typeof lng !== 'number' || !isFinite(lat) || !isFinite(lng)) {
      return Promise.resolve(null);
    }
    if (GOOGLE_MAPS_API_KEY) {
      return ensureGoogleMapsLoaded()
        .then(function () { return reverseGeocodeViaMapsJs(lat, lng); })
        .catch(function () { return null; })
        .then(function (zip5) {
          if (zip5) return zip5;
          return reverseGeocodePhoton(lat, lng);
        });
    }
    return reverseGeocodePhoton(lat, lng);
  }

  function deviceGeoRadiusOpts() {
    return { maxRadiusMiles: RADIUS_CAP_MILES + GEO_RADIUS_SLACK_MILES };
  }

  // ----- Storefront API client -----
  function storefrontFetch(query, variables) {
    if (!SHOP_DOMAIN || !STOREFRONT_TOKEN) {
      return Promise.reject(new Error('Storefront API not configured'));
    }
    return fetch('https://' + SHOP_DOMAIN + '/api/' + STOREFRONT_API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query: query, variables: variables || {} })
    }).then(function (response) {
      return response.text().then(function (text) {
        var json = null;
        try {
          json = text ? JSON.parse(text) : null;
        } catch (error) {
          dbg('Storefront response is not JSON', { httpStatus: response.status, ok: response.ok, snippet: String(text).slice(0, 200) });
          return { _httpError: true, status: response.status, bodySnippet: String(text).slice(0, 500) };
        }
        if (!response.ok && dbgEnabled()) {
          dbg('Storefront HTTP error', { status: response.status, errors: json && json.errors, keys: json ? Object.keys(json) : [] });
        }
        if (json && json.errors && json.errors.length) {
          dbg('Storefront GraphQL errors:', json.errors);
        }
        if (dbgEnabled()) {
          dbg('GraphQL response', {
            httpOk: response.ok,
            hasData: !!(json && json.data),
            dataKeys: json && json.data ? Object.keys(json.data) : [],
            errorCount: json && json.errors ? json.errors.length : 0
          });
          if (json && !json.data) {
            dbg('Storefront: missing `data` — full errors:', json.errors || '(none)');
          }
        }
        return json;
      });
    });
  }

  // ----- Checkout pickup preference -----
  var MUTATION_SET_PICKUP_PREF = [
    'mutation SetPickupPreference($cartId: ID!, $locationId: String!) {',
    '  cartBuyerIdentityUpdate(',
    '    cartId: $cartId',
    '    buyerIdentity: {',
    '      preferences: {',
    '        delivery: {',
    '          deliveryMethod: [PICK_UP]',
    '          pickupHandle: [$locationId]',
    '        }',
    '      }',
    '    }',
    '  ) {',
    '    cart { id }',
    '    userErrors { field message }',
    '  }',
    '}'
  ].join('\n');

  var MUTATION_CLEAR_PICKUP_PREF = [
    'mutation ClearPickupPreference($cartId: ID!) {',
    '  cartBuyerIdentityUpdate(',
    '    cartId: $cartId',
    '    buyerIdentity: {',
    '      preferences: {',
    '        delivery: {',
    '          pickupHandle: []',
    '        }',
    '      }',
    '    }',
    '  ) {',
    '    cart { id }',
    '    userErrors { field message }',
    '  }',
    '}'
  ].join('\n');

  function fetchAjaxCart() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (response) {
      if (!response.ok) throw new Error('cart.js HTTP ' + response.status);
      return response.json();
    });
  }

  function buildStorefrontCartId(cart) {
    if (!cart || cart.token == null || cart.token === '') return null;
    var gid = 'gid://shopify/Cart/' + String(cart.token);
    if (cart.key != null && cart.key !== '') gid += '?key=' + String(cart.key);
    return gid;
  }

  function ajaxCartHasItems(cart) {
    if (!cart) return false;
    if (cart.item_count > 0) return true;
    return !!(cart.items && cart.items.length > 0);
  }

  function pickupPrefSyncedStoreId() {
    try { return window.sessionStorage.getItem(SS_PICKUP_PREF_STORE) || ''; } catch (error) { return ''; }
  }

  function pickupPrefMarkSynced(storeId) {
    try { window.sessionStorage.setItem(SS_PICKUP_PREF_STORE, String(storeId)); } catch (error) {}
  }

  function pickupPrefClearSynced() {
    try { window.sessionStorage.removeItem(SS_PICKUP_PREF_STORE); } catch (error) {}
  }

  function pickupPrefSyncSucceeded(resp) {
    var payload = resp && resp.data && resp.data.cartBuyerIdentityUpdate;
    if (!payload) return false;
    return !(payload.userErrors && payload.userErrors.length);
  }

  function logPickupPrefResult(label, resp) {
    if (!dbgEnabled()) return;
    if (!resp || resp._httpError) {
      dbg(label + ': request failed', resp);
      return;
    }
    if (resp.errors && resp.errors.length) {
      dbg(label + ': GraphQL errors', resp.errors);
      return;
    }
    var payload = resp.data && resp.data.cartBuyerIdentityUpdate;
    if (!payload) {
      dbg(label + ': missing cartBuyerIdentityUpdate payload');
      return;
    }
    if (payload.userErrors && payload.userErrors.length) {
      dbg(label + ': userErrors', payload.userErrors);
      return;
    }
    dbg(label + ': ok');
  }

  // Tell checkout which pickup location to preselect (silent on failure).
  function syncPickupPreference(store, opts) {
    opts = opts || {};
    if (!store || store.id == null || store.id === '') return Promise.resolve();
    if (!initRefs() || !STOREFRONT_TOKEN) return Promise.resolve();
    var storeId = String(store.id);
    if (!opts.force && pickupPrefSyncedStoreId() === storeId) {
      if (dbgEnabled()) dbg('pickupPref set: skip — already synced this session', storeId);
      return Promise.resolve();
    }
    var cartPromise = opts.cart ? Promise.resolve(opts.cart) : fetchAjaxCart();
    return cartPromise
      .then(function (cart) {
        if (!opts.force && !ajaxCartHasItems(cart)) {
          if (dbgEnabled()) dbg('pickupPref set: skip — empty cart');
          return;
        }
        var cartId = buildStorefrontCartId(cart);
        if (!cartId) {
          if (dbgEnabled()) dbg('pickupPref set: skip — no cart token/key');
          return;
        }
        return storefrontFetch(MUTATION_SET_PICKUP_PREF, {
          cartId: cartId,
          locationId: storeId
        });
      })
      .then(function (resp) {
        if (!resp) return;
        logPickupPrefResult('pickupPref set', resp);
        if (pickupPrefSyncSucceeded(resp)) pickupPrefMarkSynced(storeId);
      })
      .catch(function (err) {
        if (dbgEnabled()) dbg('pickupPref set: failed', err);
      });
  }

  function clearPickupPreference() {
    pickupPrefClearSynced();
    if (!initRefs() || !STOREFRONT_TOKEN) return Promise.resolve();
    return fetchAjaxCart()
      .then(function (cart) {
        if (!ajaxCartHasItems(cart)) {
          if (dbgEnabled()) dbg('pickupPref clear: skip — empty cart');
          return;
        }
        var cartId = buildStorefrontCartId(cart);
        if (!cartId) {
          if (dbgEnabled()) dbg('pickupPref clear: skip — no cart token/key');
          return;
        }
        return storefrontFetch(MUTATION_CLEAR_PICKUP_PREF, { cartId: cartId });
      })
      .then(function (resp) {
        if (resp) logPickupPrefResult('pickupPref clear', resp);
      })
      .catch(function (err) {
        if (dbgEnabled()) dbg('pickupPref clear: failed', err);
      });
  }

  function onCartUpdatedForPickupPref(evt) {
    var store = WSSStore.get();
    if (!store) return;
    if (!isCartPickupModeEnabled()) return;
    var cart = evt && evt.detail && evt.detail.cart;
    if (!ajaxCartHasItems(cart)) return;
    syncPickupPreference(store, { cart: cart });
  }

  function syncSavedStorePickupPreference() {
    if (!initRefs()) return;
    if (document.querySelector('.cart.page')) {
      syncCheckoutFromCartFulfillment();
      return;
    }
    var store = WSSStore.get();
    if (store && isCartPickupModeEnabled()) syncPickupPreference(store);
  }

  // ----- Cart fulfillment method (shipping vs pickup) -----
  var MUTATION_SET_SHIPPING_PREF = [
    'mutation SetShippingPreference($cartId: ID!) {',
    '  cartBuyerIdentityUpdate(',
    '    cartId: $cartId',
    '    buyerIdentity: {',
    '      preferences: {',
    '        delivery: {',
    '          deliveryMethod: [SHIPPING]',
    '          pickupHandle: []',
    '        }',
    '      }',
    '    }',
    '  ) {',
    '    cart { id }',
    '    userErrors { field message }',
    '  }',
    '}'
  ].join('\n');

  // Shipping stays the default, but the saved store is registered as the preferred
  // pickup location. deliveryMethod and pickupHandle are independent — so if the
  // buyer switches to pickup at checkout their store is preselected even when the
  // cart toggle was off. The delivery customization function still reorders as backup.
  var MUTATION_SET_SHIPPING_PREF_WITH_PICKUP = [
    'mutation SetShippingPreferenceWithPickup($cartId: ID!, $locationId: String!) {',
    '  cartBuyerIdentityUpdate(',
    '    cartId: $cartId',
    '    buyerIdentity: {',
    '      preferences: {',
    '        delivery: {',
    '          deliveryMethod: [SHIPPING]',
    '          pickupHandle: [$locationId]',
    '        }',
    '      }',
    '    }',
    '  ) {',
    '    cart { id }',
    '    userErrors { field message }',
    '  }',
    '}'
  ].join('\n');

  // Persist the toggle choice as a cart attribute (read by the delivery customization function).
  function setCartFulfillmentMethodAttr(method) {
    CART_FULFILLMENT_METHOD = method;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: { fulfillment_method: method } })
    }).then(function (response) { return response.json(); }).then(function (cart) {
      applyCartFulfillmentMethod(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      return cart;
    }).catch(function () {});
  }

  function normalizeCartAttributeValue(value) {
    if (value == null) return '';
    return String(value).trim();
  }

  function applyCartFulfillmentMethod(cart) {
    var attrs = (cart && cart.attributes) || {};
    var method = normalizeCartAttributeValue(attrs[CART_ATTR_FULFILLMENT]);
    if (method === 'pickup' || method === 'shipping') {
      CART_FULFILLMENT_METHOD = method;
    }
    return CART_FULFILLMENT_METHOD;
  }

  function loadCartFulfillmentMethod() {
    return fetchAjaxCart().then(function (cart) {
      return applyCartFulfillmentMethod(cart);
    }).catch(function () { return CART_FULFILLMENT_METHOD; });
  }

  // Mirror the cart's fulfillment toggle (pickup vs shipping) back into the theme
  // when returning from checkout. Store location is intentionally not synced here.
  function hydrateThemeStoreStateFromCart(cart) {
    applyCartFulfillmentMethod(cart);
    hydrateTriggers();
    renderCartPickupHeaders();
  }

  function syncCartStateFromServer(cart, opts) {
    opts = opts || {};
    hydrateThemeStoreStateFromCart(cart);
    var onCartPage = !!document.querySelector('.cart.page');
    if (onCartPage) {
      renderCartPickupToggles();
    }
    if (onCartPage || isCartPickupModeEnabled()) {
      hydrateCartPickupLines();
      updateCartPickupUnavailableBanner();
    }
    if (!onCartPage) return Promise.resolve();
    if (opts.skipCheckoutPush) return Promise.resolve();
    return syncCheckoutFromCartFulfillment();
  }

  // Pickup mode is on only when the customer has explicitly chosen pickup.
  function isCartPickupModeEnabled() {
    if (!hasSelectedPickupStore()) return false;
    return CART_FULFILLMENT_METHOD === 'pickup';
  }

  // Quick cart has no toggle, but still respects the cart fulfillment_method attribute.
  function isPickupEnabledForLine(line) {
    return isCartPickupModeEnabled();
  }

  function shouldForcePickupOnStoreSet() {
    return CART_FULFILLMENT_METHOD === 'pickup';
  }

  // Prefill checkout with shipping. When a store is saved, also register it as the
  // preferred pickup location so switching to pickup at checkout preselects it.
  function syncShippingPreference(store) {
    pickupPrefClearSynced();
    if (!initRefs() || !STOREFRONT_TOKEN) return Promise.resolve();
    if (store === undefined) store = WSSStore.get();
    var locationId = store && store.id != null && store.id !== '' ? String(store.id) : '';
    return fetchAjaxCart()
      .then(function (cart) {
        if (!ajaxCartHasItems(cart)) {
          if (dbgEnabled()) dbg('shippingPref set: skip — empty cart');
          return;
        }
        var cartId = buildStorefrontCartId(cart);
        if (!cartId) {
          if (dbgEnabled()) dbg('shippingPref set: skip — no cart token/key');
          return;
        }
        if (locationId) {
          return storefrontFetch(MUTATION_SET_SHIPPING_PREF_WITH_PICKUP, {
            cartId: cartId,
            locationId: locationId
          });
        }
        return storefrontFetch(MUTATION_SET_SHIPPING_PREF, { cartId: cartId });
      })
      .then(function (resp) {
        if (resp) logPickupPrefResult('shippingPref set', resp);
      })
      .catch(function (err) {
        if (dbgEnabled()) dbg('shippingPref set: failed', err);
      });
  }

  // Cart attribute is source of truth — re-push buyer identity so checkout matches the toggle.
  function syncCheckoutFromCartFulfillment(opts) {
    opts = opts || {};
    if (!initRefs()) return Promise.resolve();
    var force = opts.force !== false;
    var store = WSSStore.get();
    if (isCartPickupModeEnabled() && store) {
      return syncPickupPreference(store, { force: force });
    }
    if (CART_FULFILLMENT_METHOD === 'shipping') {
      return syncShippingPreference();
    }
    return Promise.resolve();
  }

  function bindCartCheckoutPreferenceSync() {
    if (document.documentElement.dataset.wssCheckoutSyncBound === '1') return;
    document.documentElement.dataset.wssCheckoutSyncBound = '1';
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('.cart.page form[data-form]')) return;
      if (skipCheckoutSync) {
        skipCheckoutSync = false;
        return;
      }
      var submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout' || submitter.disabled) return;
      event.preventDefault();
      if (hasCartPickupUnavailableError() && !skipPickupModal) {
        pendingCheckoutSubmitter = submitter;
        openCartPickupUnavailableModal();
        return;
      }
      proceedCartCheckout(form, submitter);
    });
  }

  function proceedCartCheckout(form, submitter, opts) {
    opts = opts || {};
    var syncPromise = opts.skipPreferenceSync
      ? Promise.resolve()
      : syncCheckoutFromCartFulfillment({ force: true });
    syncPromise.finally(function () {
      skipCheckoutSync = true;
      skipPickupModal = false;
      pendingCheckoutSubmitter = null;
      if (opts.skipPreferenceSync) markCartPickupModalNavigatedAway();
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter);
      } else {
        HTMLFormElement.prototype.submit.call(form);
      }
    });
  }

  function onStoreStatePageShow(opts) {
    opts = opts || {};
    return fetchAjaxCart()
      .then(function (cart) {
        return syncCartStateFromServer(cart, opts);
      })
      .catch(function () {});
  }

  function onStoreStatePageShowAlways() {
    onStoreStatePageShow({ skipCheckoutPush: true });
  }

  var LOCATIONS_QUERY = [
    'query Locations {',
    '  locations(first: 150) {',
    '    nodes {',
    '      id',
    '      name',
    '      address {',
    '        address1 address2 city province zip phone country',
    '        latitude longitude',
    '      }',
    '      hours: metafield(namespace: "custom", key: "store_hours") { value }',
    '      displayNameStore: metafield(namespace: "store", key: "display_name") { value }',
    '      displayNameCustom: metafield(namespace: "custom", key: "display_name") { value }',
    '      latMeta: metafield(namespace: "store", key: "lat") { value }',
    '      lngMeta: metafield(namespace: "store", key: "lng") { value }',
    '      placeIdMeta: metafield(namespace: "custom", key: "placeid") { value }',
    '      storePageCustom: metafield(namespace: "custom", key: "store_page") {',
    '        value',
    '        type',
    '        reference {',
    '          ... on Page {',
    '            id',
    '            handle',
    '            onlineStoreUrl',
    '            placeId: metafield(namespace: "custom", key: "placeid") { value }',
    '          }',
    '        }',
    '        references(first: 1) {',
    '          nodes {',
    '            ... on Page {',
    '              id',
    '              handle',
    '              onlineStoreUrl',
    '              placeId: metafield(namespace: "custom", key: "placeid") { value }',
    '            }',
    '          }',
    '        }',
    '      }',
    '      storePageStore: metafield(namespace: "store", key: "store_page") {',
    '        value',
    '        type',
    '        reference {',
    '          ... on Page {',
    '            id',
    '            handle',
    '            onlineStoreUrl',
    '            placeId: metafield(namespace: "custom", key: "placeid") { value }',
    '          }',
    '        }',
    '        references(first: 1) {',
    '          nodes {',
    '            ... on Page {',
    '              id',
    '              handle',
    '              onlineStoreUrl',
    '              placeId: metafield(namespace: "custom", key: "placeid") { value }',
    '            }',
    '          }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  var STORE_PAGES_BY_GID_QUERY = [
    'query StorePagesByGid($ids: [ID!]!) {',
    '  nodes(ids: $ids) {',
    '    ... on Page {',
    '      id',
    '      handle',
    '      onlineStoreUrl',
    '      placeId: metafield(namespace: "custom", key: "placeid") { value }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  var AVAILABILITY_QUERY = [
    'query StoreAvailability($id: ID!) {',
    '  node(id: $id) {',
    '    ... on ProductVariant {',
    '      id sku',
    '      storeAvailability(first: 100) {',
    '        edges {',
    '          node {',
    '            available',
    '            quantityAvailable',
    '            location { id }',
    '          }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  // undefined = not loaded yet; cleared when flyout opens.
  var __locationsCache;
  function fetchLocations() {
    if (__locationsCache !== undefined) return Promise.resolve(__locationsCache);
    return storefrontFetch(LOCATIONS_QUERY).then(function (resp) {
      var nodes = (resp && resp.data && resp.data.locations && resp.data.locations.nodes) || [];
      if (dbgEnabled()) {
        dbg('locations query: raw node count =', nodes.length);
        if (resp && resp.data && resp.data.locations === null) {
          dbg('locations field is null — check token scope (unauthenticated_read_product_pickup_locations) and API version');
        }
        if (nodes[0]) {
          var sampleNode = nodes[0];
          dbg('first raw node', {
            id: sampleNode.id,
            name: sampleNode.name,
            hasAddress: !!sampleNode.address,
            latMeta: sampleNode.latMeta,
            lngMeta: sampleNode.lngMeta,
            displayNameCustom: sampleNode.displayNameCustom,
            displayNameStore: sampleNode.displayNameStore,
            storePageCustom: sampleNode.storePageCustom,
            placeIdMeta: sampleNode.placeIdMeta
          });
        }
      }
      var normalized = nodes.map(normalizeLocation).filter(Boolean);
      return enrichLocationsWithStorePages(normalized).then(function (enriched) {
        __locationsCache = enriched;
        if (dbgEnabled() && nodes.length && __locationsCache.length === 0) {
          dbg('WARNING: every location was dropped in normalizeLocation (check id / required fields)');
          nodes.forEach(function (rawNode, nodeIndex) {
            dbg('  raw[' + nodeIndex + ']', { id: rawNode && rawNode.id, name: rawNode && rawNode.name, hasAddress: !!(rawNode && rawNode.address) });
          });
        }
        if (dbgEnabled()) {
          dbg('normalized location count =', __locationsCache.length);
          if (__locationsCache[0]) {
            var L = __locationsCache[0];
            dbg('first normalized', {
              id: L.id,
              name: L.name,
              displayName: L.displayName,
              zip: L.zip,
              lat: L.lat,
              lng: L.lng,
              hasValidCoords: hasValidCoords(L),
              pageUrl: L.pageUrl,
              pageGid: L.pageGid,
              placeId: L.placeId
            });
          }
        }
        return __locationsCache;
      });
    });
  }

  function getPageReferenceFromMetafield(storePageMf) {
    if (!storePageMf) return null;
    var ref = storePageMf.reference;
    if (ref && (ref.handle || ref.onlineStoreUrl || ref.id)) return ref;
    var nodes = storePageMf.references && storePageMf.references.nodes;
    if (nodes && nodes.length && nodes[0]) return nodes[0];
    return null;
  }

  function storePageMetafieldHasData(storePageMf) {
    if (!storePageMf) return false;
    if (getPageReferenceFromMetafield(storePageMf)) return true;
    var raw = storePageMf.value != null ? String(storePageMf.value).trim() : '';
    return !!raw;
  }

  function pageReferenceToUrl(ref) {
    if (!ref) return '';
    if (ref.onlineStoreUrl) return ref.onlineStoreUrl;
    if (ref.handle) return '/pages/' + ref.handle;
    return '';
  }

  function extractPageGidFromMetafield(storePageMf) {
    if (!storePageMf) return '';
    var ref = getPageReferenceFromMetafield(storePageMf);
    if (ref && ref.id) return ref.id;
    var raw = storePageMf.value != null ? String(storePageMf.value).trim() : '';
    if (/^gid:\/\/shopify\/Page\//i.test(raw)) return raw;
    return '';
  }

  function resolvePlaceIdFromPageReference(ref) {
    if (!ref || !ref.placeId || ref.placeId.value == null) return '';
    return String(ref.placeId.value).trim();
  }

  function enrichLocationsWithStorePages(locations) {
    var gids = [];
    locations.forEach(function (loc) {
      if (loc.pageUrl || !loc.pageGid) return;
      gids.push(loc.pageGid);
    });
    var unique = gids.filter(function (gid, index) { return gids.indexOf(gid) === index; });
    if (!unique.length) return Promise.resolve(locations);

    return storefrontFetch(STORE_PAGES_BY_GID_QUERY, { ids: unique }).then(function (resp) {
      var pageNodes = (resp && resp.data && resp.data.nodes) || [];
      var byGid = {};
      pageNodes.forEach(function (pageNode) {
        if (pageNode && pageNode.id) byGid[pageNode.id] = pageNode;
      });
      locations.forEach(function (loc) {
        if (loc.pageUrl || !loc.pageGid) return;
        var page = byGid[loc.pageGid];
        if (!page) return;
        loc.pageUrl = pageReferenceToUrl(page);
        if (!loc.placeId) loc.placeId = resolvePlaceIdFromPageReference(page);
      });
      if (dbgEnabled()) {
        dbg('enrichLocationsWithStorePages', {
          requested: unique.length,
          resolved: pageNodes.filter(Boolean).length
        });
      }
      return locations;
    });
  }

  function parseMetafieldCoord(mf) {
    if (!mf || mf.value == null || String(mf.value).trim() === '') return null;
    var parsedValue = parseFloat(String(mf.value).trim());
    return typeof parsedValue === 'number' && !isNaN(parsedValue) && isFinite(parsedValue) ? parsedValue : null;
  }

  function resolveStorePageUrl(storePageMf) {
    if (!storePageMf) return '';
    var url = pageReferenceToUrl(getPageReferenceFromMetafield(storePageMf));
    if (url) return url;
    var rawValue = storePageMf.value != null ? String(storePageMf.value).trim() : '';
    if (!rawValue) return '';
    if (/^https?:\/\//i.test(rawValue) || rawValue.charAt(0) === '/') return rawValue;
    if (/^[a-z0-9][a-z0-9-_]*$/i.test(rawValue)) return '/pages/' + rawValue;
    return '';
  }

  function resolvePlaceIdFromStorePage(storePageMf, locationPlaceIdMf) {
    var fromPage = resolvePlaceIdFromPageReference(getPageReferenceFromMetafield(storePageMf));
    if (fromPage) return fromPage;
    if (locationPlaceIdMf && locationPlaceIdMf.value != null) {
      return String(locationPlaceIdMf.value).trim();
    }
    return '';
  }

  function pickStorePageMetafield(node) {
    if (!node) return null;
    if (storePageMetafieldHasData(node.storePageCustom)) return node.storePageCustom;
    if (storePageMetafieldHasData(node.storePageStore)) return node.storePageStore;
    return node.storePageCustom || node.storePageStore || null;
  }

  function normalizeLocation(node) {
    if (!node || !node.id) {
      dbg('normalizeLocation skip: missing node or id', node);
      return null;
    }
    var addr = node.address || {};
    if (!node.address && dbgEnabled()) {
      dbg('normalizeLocation: address is null — using metafields / empty address fields', { id: node.id, name: node.name });
    }
    var mLat = parseMetafieldCoord(node.latMeta);
    var mLng = parseMetafieldCoord(node.lngMeta);
    var lat;
    var lng;
    if (mLat != null && mLng != null) {
      lat = mLat;
      lng = mLng;
    } else {
      lat = addr.latitude;
      lng = addr.longitude;
      if (typeof lat === 'string') lat = parseFloat(lat);
      if (typeof lng === 'string') lng = parseFloat(lng);
      if (!(typeof lat === 'number' && !isNaN(lat) && isFinite(lat)) ||
          !(typeof lng === 'number' && !isNaN(lng) && isFinite(lng))) {
        lat = null;
        lng = null;
      }
    }
    var idMatch = String(node.id).match(/Location\/(\d+)/);
    var storePageMf = pickStorePageMetafield(node);
    return {
      gid: node.id,
      id: idMatch ? idMatch[1] : node.id,
      name: node.name || '',
      displayName: (node.displayNameCustom && node.displayNameCustom.value) ||
        (node.displayNameStore && node.displayNameStore.value) ||
        node.name || '',
      address1: addr.address1 || '',
      address2: addr.address2 || '',
      city: addr.city || '',
      state: addr.province || '',
      zip: addr.zip || '',
      country: addr.country || '',
      phone: addr.phone || '',
      lat: lat,
      lng: lng,
      hoursRaw: node.hours && node.hours.value ? node.hours.value : null,
      pageGid: extractPageGidFromMetafield(storePageMf),
      pageUrl: resolveStorePageUrl(storePageMf),
      placeId: resolvePlaceIdFromStorePage(storePageMf, node.placeIdMeta)
    };
  }

  function fetchAvailability(variantGid) {
    if (!variantGid) return Promise.resolve({});
    return storefrontFetch(AVAILABILITY_QUERY, { id: variantGid })
      .then(function (resp) {
        var node = resp && resp.data && resp.data.node;
        var edges = (node && node.storeAvailability && node.storeAvailability.edges) || [];
        var map = {};
        edges.forEach(function (edge) {
          var availabilityNode = edge.node;
          if (!availabilityNode || !availabilityNode.location || !availabilityNode.location.id) return;
          var entry = {
            available: !!availabilityNode.available,
            quantity: typeof availabilityNode.quantityAvailable === 'number' ? availabilityNode.quantityAvailable : null
          };
          map[availabilityNode.location.id] = entry;
          var num = shopifyLocationNumericId(availabilityNode.location.id);
          if (num) map['gid://shopify/Location/' + num] = entry;
        });
        return map;
      })
      .catch(function () { return {}; });
  }

  function availabilityEntryForStore(store) {
    if (!store) return null;
    if (store.gid && availabilityMap[store.gid]) return availabilityMap[store.gid];
    var lid = 'gid://shopify/Location/' + String(store.id).replace(/^gid:\/\/shopify\/Location\//, '');
    if (availabilityMap[lid]) return availabilityMap[lid];
    var num = shopifyLocationNumericId(store.gid) || shopifyLocationNumericId(store.id);
    return num ? availabilityMap['gid://shopify/Location/' + num] || null : null;
  }

  function pickupAvailabilityStatus(entry, requestedQty) {
    requestedQty = parseInt(requestedQty, 10);
    if (isNaN(requestedQty) || requestedQty < 1) requestedQty = 1;
    var storeQty = null;
    if (!entry || !entry.available) {
      return { status: 'red', storeQty: storeQty, requestedQty: requestedQty };
    }
    if (typeof entry.quantity === 'number') {
      storeQty = entry.quantity;
      if (storeQty <= 0) {
        return { status: 'red', storeQty: storeQty, requestedQty: requestedQty };
      }
      if (storeQty < requestedQty) {
        return { status: 'yellow', storeQty: storeQty, requestedQty: requestedQty };
      }
    }
    return { status: 'green', storeQty: storeQty, requestedQty: requestedQty };
  }

  function statusLabel(status) {
    return status === 'green' ? 'Available' : 'Unavailable';
  }

  function locationGidFromStore(store) {
    if (!store) return null;
    var storeGid = store.gid;
    if (storeGid && String(storeGid).indexOf('gid://shopify/Location/') === 0) return storeGid;
    var id = store.id;
    if (id == null || id === '') return null;
    var sid = String(id);
    if (sid.indexOf('gid://shopify/Location/') === 0) return sid;
    return 'gid://shopify/Location/' + sid;
  }

  function cartPickupAvailabilityCacheKey(gid, store) {
    if (!gid) return '';
    return gid + '|' + (locationGidFromStore(store) || '');
  }

  function hasCachedCartPickupAvailability(gid, store) {
    var key = cartPickupAvailabilityCacheKey(gid, store);
    return !!(key && Object.prototype.hasOwnProperty.call(cartPickupAvailabilityByGid, key));
  }

  function getCachedCartPickupAvailability(gid, store) {
    var key = cartPickupAvailabilityCacheKey(gid, store);
    if (!key || !Object.prototype.hasOwnProperty.call(cartPickupAvailabilityByGid, key)) return undefined;
    return cartPickupAvailabilityByGid[key];
  }

  function setCachedCartPickupAvailability(gid, store, entry) {
    var key = cartPickupAvailabilityCacheKey(gid, store);
    if (!key) return;
    cartPickupAvailabilityByGid[key] = entry;
  }

  // ----- Cart pickup -----
  function cartStoreDisplayLabel(store) {
    if (!store) return '';
    return store.displayName || store.name || '';
  }

  function hasSelectedPickupStore(store) {
    var selectedStore = store === undefined ? WSSStore.get() : store;
    return !!(selectedStore && (selectedStore.displayName || selectedStore.name));
  }

  function getAllCartPickupLines() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-cart-pickup-line]'));
  }

  function getCartLineQuantity(line) {
    if (!line) return 1;
    var row = line.closest('[data-input-item]');
    if (row) {
      var qtyInput = row.querySelector('.quantity-input__input, [data-quantity-input]');
      if (qtyInput) {
        var parsed = parseInt(qtyInput.value, 10);
        if (!isNaN(parsed) && parsed >= 1) return parsed;
      }
    }
    var dataQty = line.dataset.lineQuantity;
    if (dataQty) {
      var fromData = parseInt(dataQty, 10);
      if (!isNaN(fromData) && fromData >= 1) return fromData;
    }
    return 1;
  }

  function isCartPickupMobileLayout(line) {
    if (line && line.closest('.quick-cart')) return true;
    var mobileViewport = window.matchMedia('(max-width: 959px)').matches;
    if (!mobileViewport) return false;
    if (!line) return false;
    return !!line.closest('.cart.page');
  }

  function cartStatusLabelHtml(resolved, mobileLayout) {
    var status = resolved.status;
    var storeQty = resolved.storeQty;
    var requestedQty = resolved.requestedQty;
    if (mobileLayout) {
      if (status === 'green') {
        return '<span class="cart-pickup-line__status">Available</span> for pickup';
      }
      if (status === 'yellow') {
        return '<span class="cart-pickup-line__status">' + storeQty + ' of ' + requestedQty + ' available</span> for pickup';
      }
      return '<span class="cart-pickup-line__status">Unavailable</span> for pickup';
    }
    if (status === 'green') {
      return '<span class="cart-pickup-line__status">Available</span> for pickup at ';
    }
    if (status === 'yellow') {
      return '<span class="cart-pickup-line__status">' + storeQty + ' of ' + requestedQty + ' available</span> at ';
    }
    return '<span class="cart-pickup-line__status">Unavailable</span> for pickup at ';
  }

  var SS_OPENED_LOCATOR_FROM_QUICK_CART = 'wss_opened_locator_from_quick_cart';
  var SS_OPENED_LOCATOR_FROM_CART = 'wss_opened_locator_from_cart';
  // In-memory cart/quick-cart locator context survives flyout close timing.
  var locatorOpenedFromCartUi = null;
  var APPLY_BTN_LOADING_LABEL = 'Applying...';
  var APPLY_BTN_DEFAULT_LABEL = 'Make this my store';
  var applyBtnDefaultLabel = APPLY_BTN_DEFAULT_LABEL;

  function resolveStoreLocatorContext(context) {
    if (pdpTileStockEnabled(context)) return context;
    if (isMainProductTemplatePage()) {
      var pdpCtx = headerOpenStoreLocatorContext();
      if (pdpCtx) return pdpCtx;
    }
    return context || null;
  }

  function setLocatorOpenedFromCartUi(kind) {
    locatorOpenedFromCartUi = kind;
    try {
      if (kind === 'cart') sessionStorage.setItem(SS_OPENED_LOCATOR_FROM_CART, '1');
      if (kind === 'quick-cart') sessionStorage.setItem(SS_OPENED_LOCATOR_FROM_QUICK_CART, '1');
    } catch (error) {}
  }

  function clearLocatorOpenedFromCartUi() {
    locatorOpenedFromCartUi = null;
    try {
      sessionStorage.removeItem(SS_OPENED_LOCATOR_FROM_CART);
      sessionStorage.removeItem(SS_OPENED_LOCATOR_FROM_QUICK_CART);
    } catch (error) {}
  }

  function openStoreLocatorFromCart(context) {
    if (document.querySelector('.cart.page')) {
      setLocatorOpenedFromCartUi('cart');
    } else if (document.querySelector('.quick-cart__wrapper.active')) {
      setLocatorOpenedFromCartUi('quick-cart');
      document.dispatchEvent(new CustomEvent('quick-cart:close'));
    }
    if (typeof window.openStoreLocator === 'function') {
      window.openStoreLocator(resolveStoreLocatorContext(context));
    }
  }

  function bindCartPickupClick(el) {
    if (!el || el.__wssCartPickupBound) return;
    el.__wssCartPickupBound = true;
    el.addEventListener('click', function (event) {
      event.preventDefault();
      openStoreLocatorFromCart();
    });
  }

  function getCartPickupConfig() {
    var header = document.querySelector('[data-cart-pickup-header]');
    if (!header) return null;
    return {
      domain: header.dataset.shopDomain,
      token: header.dataset.storefrontToken,
      lines: Array.prototype.slice.call(document.querySelectorAll('[data-cart-pickup-line]'))
    };
  }

  function renderCartPickupHeaders() {
    var store = WSSStore.get();
    var linkText = store && (store.displayName || store.name)
      ? cartStoreDisplayLabel(store)
      : 'Select a Store';
    document.querySelectorAll('[data-cart-pickup-header-link]').forEach(function (link) {
      link.textContent = linkText;
      if (store && (store.displayName || store.name)) {
        link.setAttribute('title', linkText);
      } else {
        link.removeAttribute('title');
      }
      bindCartPickupClick(link);
    });
  }

  // ----- Cart fulfillment toggle (shipping vs pickup) -----
  function getCartPickupToggles() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-cart-pickup-toggle]'));
  }

  function renderCartPickupToggles() {
    var on = isCartPickupModeEnabled();
    getCartPickupToggles().forEach(function (toggle) {
      toggle.setAttribute('aria-checked', on ? 'true' : 'false');
      toggle.classList.toggle('cart-pickup-toggle--on', on);
      bindCartPickupToggle(toggle);
    });
  }

  function bindCartPickupToggle(toggle) {
    if (!toggle || toggle.__wssToggleBound) return;
    toggle.__wssToggleBound = true;
    toggle.addEventListener('click', function (event) {
      event.preventDefault();
      if (isCartPickupModeEnabled()) {
        disableCartPickup();
      } else {
        enableCartPickup();
      }
    });
  }

  // Turn pickup ON. With a saved store, switch immediately; otherwise open the
  // store locator and finish once a store is chosen (onStoreChangedForCartToggle).
  function enableCartPickup() {
    var store = WSSStore.get();
    if (!hasSelectedPickupStore(store)) {
      pendingEnablePickup = true;
      openStoreLocatorFromCart();
      return;
    }
    setCartFulfillmentMethodAttr('pickup').then(function () {
      syncPickupPreference(store, { force: true });
    });
    renderCartPickupToggles();
    syncAllCartPickupSurfaces();
  }

  // Turn pickup OFF. Keeps the saved store; only flips checkout to shipping and
  // hides the per-line availability + unavailable banner.
  function disableCartPickup() {
    var updatePromise = setCartFulfillmentMethodAttr('shipping');
    renderCartPickupToggles();
    hideAllCartPickupLines(getAllCartPickupLines());
    updateCartPickupUnavailableBanner();
    return Promise.all([updatePromise, syncShippingPreference()]);
  }

  function isCartPickupContext() {
    return !!(
      document.querySelector('.cart.page [data-cart-pickup-header]') ||
      document.querySelector('[data-cart-pickup-toggle]')
    );
  }

  function wasStoreLocatorOpenedFromQuickCart() {
    if (locatorOpenedFromCartUi === 'quick-cart') return true;
    try {
      return sessionStorage.getItem(SS_OPENED_LOCATOR_FROM_QUICK_CART) === '1';
    } catch (error) {
      return false;
    }
  }

  function enableCartPickupAfterStoreSelection(store) {
    if (!hasSelectedPickupStore(store)) return;
    CART_FULFILLMENT_METHOD = 'pickup';
    setCartFulfillmentMethodAttr('pickup').then(function () {
      syncPickupPreference(store, { force: true });
    });
    renderCartPickupHeaders();
    renderCartPickupToggles();
    syncAllCartPickupSurfaces();
  }

  function onStoreChangedForCartToggle(evt) {
    var detail = evt && evt.detail ? evt.detail : {};
    var store = detail.store || WSSStore.get();

    if (pendingEnablePickup && hasSelectedPickupStore(store)) {
      pendingEnablePickup = false;
      enableCartPickupAfterStoreSelection(store);
    }
  }

  function cartPickupCheckingMessage() {
    var header = document.querySelector('[data-cart-pickup-header]');
    if (header && header.dataset.msgChecking) return header.dataset.msgChecking;
    return 'Checking availability…';
  }

  function lineHasFreshPickupStatus(line, store) {
    if (!line || !line.hasAttribute('data-status')) return false;
    if (line.hasAttribute('data-fetching')) return false;
    var storeId = store && store.id != null ? String(store.id) : '';
    if (!storeId) return false;
    return line.getAttribute('data-pickup-store-id') === storeId;
  }

  function setCartPickupLineLoading(line, msg) {
    if (!isPickupEnabledForLine(line)) {
      line.hidden = true;
      return;
    }
    line.hidden = false;
    // Keep the current label visible when re-fetching to avoid layout shift.
    if (line.hasAttribute('data-status')) {
      return;
    }
    line.classList.add('cart-pickup-line--loading');
    line.setAttribute('data-fetching', 'true');
    line.setAttribute('aria-busy', 'true');
    var labelEl = line.querySelector('[data-cart-pickup-line-label]');
    var storeBtn = line.querySelector('[data-cart-pickup-line-store]');
    if (labelEl) labelEl.textContent = msg;
    if (storeBtn) {
      storeBtn.hidden = true;
      storeBtn.textContent = '';
    }
  }

  function clearCartPickupLineLoading(line) {
    line.classList.remove('cart-pickup-line--loading');
    line.removeAttribute('data-fetching');
    line.removeAttribute('aria-busy');
  }

  function hideAllCartPickupLines(lines) {
    lines.forEach(function (line) {
      line.hidden = true;
      line.removeAttribute('data-status');
      line.removeAttribute('data-pickup-store-id');
      line.removeAttribute('data-store-quantity');
      line.removeAttribute('data-cart-quantity');
      clearCartPickupLineLoading(line);
      var labelEl = line.querySelector('[data-cart-pickup-line-label]');
      var storeBtn = line.querySelector('[data-cart-pickup-line-store]');
      if (labelEl) labelEl.textContent = '';
      if (storeBtn) {
        storeBtn.hidden = true;
        storeBtn.textContent = '';
      }
    });
  }

  function renderCartPickupLine(line, store, entry) {
    if (!isPickupEnabledForLine(line)) {
      line.hidden = true;
      line.removeAttribute('data-status');
      line.removeAttribute('data-pickup-store-id');
      line.removeAttribute('data-store-quantity');
      line.removeAttribute('data-cart-quantity');
      clearCartPickupLineLoading(line);
      return;
    }
    clearCartPickupLineLoading(line);
    line.hidden = false;
    var requestedQty = getCartLineQuantity(line);
    var resolved = pickupAvailabilityStatus(entry, requestedQty);
    line.setAttribute('data-status', resolved.status);
    line.setAttribute('data-cart-quantity', String(resolved.requestedQty));
    if (typeof resolved.storeQty === 'number') {
      line.setAttribute('data-store-quantity', String(resolved.storeQty));
    } else {
      line.removeAttribute('data-store-quantity');
    }
    if (store && store.id != null && store.id !== '') {
      line.setAttribute('data-pickup-store-id', String(store.id));
    } else {
      line.removeAttribute('data-pickup-store-id');
    }
    var mobileLayout = isCartPickupMobileLayout(line);
    var labelEl = line.querySelector('[data-cart-pickup-line-label]');
    var storeBtn = line.querySelector('[data-cart-pickup-line-store]');
    if (labelEl) labelEl.innerHTML = cartStatusLabelHtml(resolved, mobileLayout);
    if (storeBtn) {
      if (mobileLayout) {
        storeBtn.hidden = true;
        storeBtn.textContent = '';
      } else {
        storeBtn.hidden = false;
        storeBtn.textContent = cartStoreDisplayLabel(store);
        bindCartPickupClick(storeBtn);
      }
    }
  }

  function resolveCartPickupLineStatus(line) {
    if (!line || !isPickupEnabledForLine(line)) return null;
    var domStatus = line.getAttribute('data-status');
    var requestedQty = getCartLineQuantity(line);
    if (domStatus === 'green' || domStatus === 'red' || domStatus === 'yellow') {
      var domStoreQty = parseInt(line.dataset.storeQuantity, 10);
      return {
        status: domStatus,
        storeQty: isNaN(domStoreQty) ? null : domStoreQty,
        requestedQty: requestedQty
      };
    }
    var gid = line.dataset.variantGid;
    var store = WSSStore.get();
    if (gid && hasCachedCartPickupAvailability(gid, store)) {
      return pickupAvailabilityStatus(getCachedCartPickupAvailability(gid, store), requestedQty);
    }
    return null;
  }

  function unfulfillableUnitsFromResolved(resolved) {
    if (!resolved || resolved.status === 'green') return 0;
    if (resolved.status === 'red') return resolved.requestedQty;
    if (resolved.status === 'yellow') {
      if (typeof resolved.storeQty === 'number' && !isNaN(resolved.storeQty)) {
        return Math.max(0, resolved.requestedQty - resolved.storeQty);
      }
      return 1;
    }
    return 0;
  }

  function countUnfulfillablePickupUnitsInContainer(container) {
    if (!container) return 0;
    var total = 0;
    var lines = container.querySelectorAll('[data-cart-pickup-line]');
    lines.forEach(function (line) {
      total += unfulfillableUnitsFromResolved(resolveCartPickupLineStatus(line));
    });
    return total;
  }

  function hasCartPickupUnavailableError() {
    if (!isCartPickupModeEnabled()) return false;
    var cartPage = document.querySelector('.cart.page');
    return countUnfulfillablePickupUnitsInContainer(cartPage) > 0;
  }

  function getCartPickupUnavailableModal() {
    return document.getElementById('cartPickupUnavailableModal');
  }

  function openCartPickupUnavailableModal() {
    var dialog = getCartPickupUnavailableModal();
    if (!dialog) return;
    if (dialog.open) return;
    dialog.style.removeProperty('display');
    clearCartPickupModalNavigatedAway();
    setCartPickupUnavailableModalLoading(false);
    try {
      dialog.showModal();
    } catch (error) {
      dialog.setAttribute('open', '');
    }
  }

  function isCartPickupUnavailableModalLoading() {
    var dialog = getCartPickupUnavailableModal();
    return !!(dialog && dialog.classList.contains('cart-pickup-unavailable-modal--loading'));
  }

  function setCartPickupUnavailableModalLoading(loading) {
    var dialog = getCartPickupUnavailableModal();
    if (!dialog) return;
    var isLoading = !!loading;
    dialog.classList.toggle('cart-pickup-unavailable-modal--loading', isLoading);
    var loadingEl = dialog.querySelector('[data-cart-pickup-unavailable-loading]');
    if (loadingEl) {
      loadingEl.hidden = !isLoading;
      loadingEl.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }
    dialog.querySelectorAll(
      '[data-cart-pickup-unavailable-close], [data-cart-pickup-unavailable-edit-cart], [data-cart-pickup-unavailable-select-store], [data-cart-pickup-unavailable-checkout]'
    ).forEach(function (el) {
      el.disabled = isLoading;
      if (isLoading) el.setAttribute('aria-disabled', 'true');
      else el.removeAttribute('aria-disabled');
    });
  }

  function isCartPickupUnavailableModalOpen() {
    var dialog = getCartPickupUnavailableModal();
    return !!(dialog && dialog.open);
  }

  function markCartPickupModalNavigatedAway() {
    if (!window.matchMedia('(max-width: 959px)').matches) return;
    document.documentElement.classList.add('wss-cart-modal-navigated-away');
    var dialog = getCartPickupUnavailableModal();
    if (dialog) dialog.style.display = 'none';
    closeCartPickupUnavailableModalForPageCache();
  }

  function clearCartPickupModalNavigatedAway() {
    document.documentElement.classList.remove('wss-cart-modal-navigated-away');
    var dialog = getCartPickupUnavailableModal();
    if (dialog) dialog.style.removeProperty('display');
  }

  function closeCartPickupUnavailableModalForPageCache() {
    setCartPickupUnavailableModalLoading(false);
    var dialog = getCartPickupUnavailableModal();
    if (!dialog) return;
    if (dialog.open) {
      try { dialog.close(); } catch (error) {}
    }
    dialog.removeAttribute('open');
  }

  function resetCartPickupUnavailableModal() {
    setCartPickupUnavailableModalLoading(false);
    skipPickupModal = false;
    pendingCheckoutSubmitter = null;
    var dialog = getCartPickupUnavailableModal();
    if (!dialog) return;
    if (dialog.open) {
      try { dialog.close(); } catch (error) {}
    }
    dialog.removeAttribute('open');
  }

  function restoreCartPageAfterNavigation(opts) {
    opts = opts || {};
    if (!document.querySelector('.cart.page')) return false;
    var hadModalCheckout = false;
    try {
      hadModalCheckout = sessionStorage.getItem(SS_CART_MODAL_CHECKOUT) === '1';
      if (hadModalCheckout) sessionStorage.removeItem(SS_CART_MODAL_CHECKOUT);
    } catch (error) {}
    if (!hadModalCheckout && !opts.persisted && !isCartPickupUnavailableModalOpen()) return false;

    var isMobile = window.matchMedia('(max-width: 959px)').matches;
    if (isMobile) document.documentElement.classList.add('wss-cart-modal-navigated-away');
    resetCartPickupUnavailableModal();
    if (isMobile) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          clearCartPickupModalNavigatedAway();
        });
      });
    }
    return true;
  }

  function bindCartPickupNavigationRecovery() {
    window.addEventListener('pagehide', function () {
      try {
        if (sessionStorage.getItem(SS_CART_MODAL_CHECKOUT) !== '1') return;
      } catch (error) {
        return;
      }
      markCartPickupModalNavigatedAway();
    });
  }

  function closeCartPickupUnavailableModal() {
    if (isCartPickupUnavailableModalLoading()) return;
    var dialog = getCartPickupUnavailableModal();
    if (!dialog || !dialog.open) return;
    dialog.close();
    if (!skipPickupModal) {
      pendingCheckoutSubmitter = null;
    }
  }

  function bindCartPickupUnavailableModal() {
    if (document.documentElement.dataset.wssPickupUnavailableModalBound === '1') return;
    document.documentElement.dataset.wssPickupUnavailableModalBound = '1';

    document.addEventListener('click', function (event) {
      var dialog = getCartPickupUnavailableModal();
      if (!dialog || !dialog.open) return;
      if (isCartPickupUnavailableModalLoading()) return;

      if (event.target === dialog) {
        event.preventDefault();
        closeCartPickupUnavailableModal();
        return;
      }
      if (event.target.closest('[data-cart-pickup-unavailable-close]')) {
        event.preventDefault();
        event.stopPropagation();
        closeCartPickupUnavailableModal();
        return;
      }
      if (event.target.closest('[data-cart-pickup-unavailable-edit-cart]')) {
        event.preventDefault();
        closeCartPickupUnavailableModal();
        return;
      }
      if (event.target.closest('[data-cart-pickup-unavailable-select-store]')) {
        event.preventDefault();
        closeCartPickupUnavailableModal();
        openStoreLocatorFromCart();
        return;
      }
      if (!event.target.closest('[data-cart-pickup-unavailable-checkout]')) return;
      event.preventDefault();
      var form = document.querySelector('.cart.page form[data-form]');
      var submitter = pendingCheckoutSubmitter;
      skipPickupModal = true;
      setCartPickupUnavailableModalLoading(true);
      try { sessionStorage.setItem(SS_CART_MODAL_CHECKOUT, '1'); } catch (error) {}
      disableCartPickup().then(function () {
        if (form && submitter) {
          proceedCartCheckout(form, submitter, { skipPreferenceSync: true });
          return;
        }
        skipPickupModal = false;
        try { sessionStorage.removeItem(SS_CART_MODAL_CHECKOUT); } catch (error) {}
        setCartPickupUnavailableModalLoading(false);
      });
    });

    document.addEventListener('cancel', function (event) {
      if (!event.target || event.target.id !== 'cartPickupUnavailableModal') return;
      if (isCartPickupUnavailableModalLoading()) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      closeCartPickupUnavailableModal();
    }, true);

    document.addEventListener('close', function (event) {
      if (!event.target || event.target.id !== 'cartPickupUnavailableModal') return;
      setCartPickupUnavailableModalLoading(false);
    }, true);
  }

  function cartPickupUnavailableBannerMessage(count) {
    var countBold = '<strong class="cart-pickup-unavailable-banner__count">' + count + '</strong>';
    if (count === 1) {
      return countBold + ' item isn\u2019t available at your selected pickup location. Choose a different store or select Shipping at checkout.';
    }
    return countBold + ' items aren\u2019t available at your selected pickup location. Choose a different store or select Shipping at checkout.';
  }

  function updateCartPickupUnavailableBanner() {
    var banners = document.querySelectorAll('.cart.page [data-cart-pickup-unavailable-banner]');
    if (!banners.length) return;
    if (!isCartPickupModeEnabled()) {
      banners.forEach(function (banner) { banner.hidden = true; });
      return;
    }
    var count = countUnfulfillablePickupUnitsInContainer(document.querySelector('.cart.page'));
    banners.forEach(function (banner) {
      if (count < 1) {
        banner.hidden = true;
        return;
      }
      banner.hidden = false;
      var textEl = banner.querySelector('[data-cart-pickup-unavailable-banner-text]');
      if (textEl) textEl.innerHTML = cartPickupUnavailableBannerMessage(count);
    });
  }

  function hydrateCartPickupAvailability(activeStore, config) {
    var domain = config.domain;
    var token = config.token;
    var allLines = config.lines;
    var lines = allLines.filter(isPickupEnabledForLine);
    var hiddenLines = allLines.filter(function (line) {
      return !isPickupEnabledForLine(line);
    });
    var fetchId = ++cartPickupAvailabilityFetchId;
    hideAllCartPickupLines(hiddenLines);
    if (!lines.length) {
      updateCartPickupUnavailableBanner();
      return Promise.resolve();
    }
    if (!domain || !token) {
      hideAllCartPickupLines(lines);
      updateCartPickupUnavailableBanner();
      return Promise.resolve();
    }
    var locationGid = locationGidFromStore(activeStore);
    if (!locationGid) {
      hideAllCartPickupLines(lines);
      updateCartPickupUnavailableBanner();
      return Promise.resolve();
    }

    var checkingMsg = cartPickupCheckingMessage();
    var byGid = {};
    lines.forEach(function (line) {
      var gid = line.dataset.variantGid;
      if (!gid) return;
      (byGid[gid] = byGid[gid] || []).push(line);
      if (hasCachedCartPickupAvailability(gid, activeStore)) {
        renderCartPickupLine(line, activeStore, getCachedCartPickupAvailability(gid, activeStore));
        return;
      }
      if (lineHasFreshPickupStatus(line, activeStore)) {
        line.hidden = false;
        return;
      }
      setCartPickupLineLoading(line, checkingMsg);
    });
    updateCartPickupUnavailableBanner();
    var query = 'query Q($id: ID!) { node(id: $id) { ... on ProductVariant { storeAvailability(first: 100) { edges { node { available quantityAvailable location { id } } } } } } }';

    var gids = Object.keys(byGid);
    if (!gids.length) {
      updateCartPickupUnavailableBanner();
      return Promise.resolve();
    }

    var gidsToFetch = gids.filter(function (gid) {
      return !hasCachedCartPickupAvailability(gid, activeStore);
    });
    if (!gidsToFetch.length) {
      return Promise.resolve();
    }

    var fetches = gidsToFetch.map(function (gid) {
      return fetch('https://' + domain + '/api/' + STOREFRONT_API_VERSION + '/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ query: query, variables: { id: gid } })
      }).then(function (response) { return response.json(); }).then(function (json) {
        var edges = json && json.data && json.data.node && json.data.node.storeAvailability && json.data.node.storeAvailability.edges;
        var match = null;
        if (edges) {
          for (var edgeIndex = 0; edgeIndex < edges.length; edgeIndex++) {
            var locId = edges[edgeIndex].node && edges[edgeIndex].node.location && edges[edgeIndex].node.location.id;
            if (locId && storeAvailabilityLocationIdsMatch(locId, locationGid)) {
              match = {
                available: !!edges[edgeIndex].node.available,
                quantity: edges[edgeIndex].node.quantityAvailable
              };
              break;
            }
          }
        }
        setCachedCartPickupAvailability(gid, activeStore, match);
        if (fetchId !== cartPickupAvailabilityFetchId) return;
        byGid[gid].forEach(function (line) {
          renderCartPickupLine(line, activeStore, match);
        });
      }).catch(function () {
        setCachedCartPickupAvailability(gid, activeStore, null);
        if (fetchId !== cartPickupAvailabilityFetchId) return;
        byGid[gid].forEach(function (line) {
          renderCartPickupLine(line, activeStore, null);
        });
      });
    });

    return Promise.all(fetches).then(function () {
      if (fetchId !== cartPickupAvailabilityFetchId) return;
      updateCartPickupUnavailableBanner();
    });
  }

  // Read pickup toggle state rendered server-side so cart.js is not required for first paint.
  function seedCartFulfillmentMethodFromDom() {
    var toggle = document.querySelector('[data-cart-pickup-toggle]');
    if (!toggle || !hasSelectedPickupStore()) return;
    if (toggle.getAttribute('aria-checked') === 'true') {
      CART_FULFILLMENT_METHOD = 'pickup';
    } else if (toggle.getAttribute('aria-checked') === 'false') {
      CART_FULFILLMENT_METHOD = 'shipping';
    }
  }

  function paintSavedStoreUi() {
    hydrateTriggers();
    renderCartPickupHeaders();
    renderCartPickupToggles();
  }

  function hydrateCartPickupLinesNow() {
    var lines = getAllCartPickupLines();
    var config = getCartPickupConfig();
    var store = WSSStore.get();
    if (!config) return;

    var hasVisiblePickupLines = lines.some(isPickupEnabledForLine);
    if (!hasVisiblePickupLines) {
      hideAllCartPickupLines(lines);
      updateCartPickupUnavailableBanner();
      return;
    }

    hydrateCartPickupAvailability(store, config);
  }

  function hydrateCartPickupLines() {
    if (hydrateCartPickupLinesTimer) clearTimeout(hydrateCartPickupLinesTimer);
    hydrateCartPickupLinesTimer = setTimeout(function () {
      hydrateCartPickupLinesTimer = null;
      hydrateCartPickupLinesNow();
    }, 50);
  }

  function getQuickCartSectionEl() {
    return document.querySelector('[data-section-type="quick-cart"]');
  }

  // Re-render quick-cart line items from the server (same path as quantity updates).
  function refreshQuickCartSection() {
    var section = getQuickCartSectionEl();
    if (!section) return Promise.resolve();
    var sectionId = section.getAttribute('data-section-id');
    if (!sectionId) return Promise.resolve();
    return fetch('/cart?section_id=' + encodeURIComponent(sectionId), {
      credentials: 'same-origin'
    }).then(function (response) { return response.text(); }).then(function (html) {
      var container = document.createElement('div');
      container.innerHTML = html;
      var currentWrapper = section.querySelector('.quick-cart__wrapper');
      var responseWrapper = container.querySelector('.quick-cart__wrapper');
      if (!currentWrapper || !responseWrapper) return;
      var currentItems = currentWrapper.querySelector('.quick-cart__items');
      var responseItems = responseWrapper.querySelector('.quick-cart__items');
      if (currentItems && responseItems) {
        currentItems.innerHTML = responseItems.innerHTML;
        return;
      }
      var currentInner = section.querySelector('.quick-cart__container');
      var responseInner = container.querySelector('.quick-cart__container');
      if (currentInner && responseInner) {
        currentInner.innerHTML = responseInner.innerHTML;
      }
    }).catch(function () {});
  }

  function syncAllCartPickupSurfaces() {
    return refreshQuickCartSection().then(function () {
      hydrateCartPickup();
    });
  }

  function hydrateCartPickup() {
    renderCartPickupHeaders();
    renderCartPickupToggles();
    refreshCartPickupLinesFromCache();
    hydrateCartPickupLines();
  }
  window.hydrateCartPickup = hydrateCartPickup;

  function refreshCartPickupLinesFromCache() {
    var store = WSSStore.get();
    var config = getCartPickupConfig();
    if (!config || !store) return;
    config.lines.forEach(function (line) {
      if (!isPickupEnabledForLine(line)) return;
      var gid = line.dataset.variantGid;
      if (!gid || !hasCachedCartPickupAvailability(gid, store)) return;
      renderCartPickupLine(line, store, getCachedCartPickupAvailability(gid, store));
    });
    updateCartPickupUnavailableBanner();
  }

  function bindCartPickupQuantityListener() {
    if (document.documentElement.dataset.wssCartPickupQtyBound === '1') return;
    document.documentElement.dataset.wssCartPickupQtyBound = '1';
    function onCartQtyChange(event) {
      var input = event.target;
      if (!input || !input.matches || !input.matches('[data-quantity-input]')) return;
      if (!input.closest('.cart.page') && !input.closest('.quick-cart')) return;
      refreshCartPickupLinesFromCache();
    }
    document.addEventListener('change', onCartQtyChange);
    document.addEventListener('input', onCartQtyChange);
  }

  // ----- Flyout -----
  var dlg, listEl, summaryEl, loadingEl, emptyEl, noResultsEl, noResultsQuery,
      footerEl, applyBtn, useLocationBtn, useLocationText,
      inputEl, submitBtn, tileTpl;
  var currentContext = null;
  var lastMainTemplateProductVariantGid = '';
  var lastMainTemplateProductVariantSku = '';
  var currentSelection = null;
  var currentResults = [];
  var currentQueryLabel = '';
  var availabilityMap = {};
  var placeAutocomplete = null;
  var placeAutocompleteBound = false;

  function attachPlacesAutocomplete() {
    if (!inputEl || !GOOGLE_MAPS_API_KEY || placeAutocompleteBound) return;
    ensureGoogleMapsLoaded()
      .then(function () {
        if (placeAutocompleteBound || !inputEl) return;
        if (!(google.maps && google.maps.places && google.maps.places.Autocomplete)) {
          if (dbgEnabled()) dbg('Places Autocomplete unavailable');
          return;
        }
        placeAutocompleteBound = true;
        placeAutocomplete = new google.maps.places.Autocomplete(inputEl, {
          fields: ['geometry', 'formatted_address', 'name'],
          componentRestrictions: { country: 'us' }
        });
        google.maps.event.addListener(placeAutocomplete, 'place_changed', function () {
          var place = placeAutocomplete.getPlace();
          if (!place || !place.geometry || !place.geometry.location) return;
          var loc = place.geometry.location;
          var lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
          var lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
          var label = place.formatted_address || place.name || (inputEl && inputEl.value) || '';
          runSearch((inputEl && inputEl.value) ? inputEl.value.trim() : '', {
            presetGeo: { lat: lat, lng: lng, label: label }
          });
        });
      })
      .catch(function () {});
  }

  function parseRadiusCapMiles(raw) {
    var parsed = Number(raw);
    if (!isFinite(parsed)) return RADIUS_CAP_MILES_DEFAULT;
    return Math.min(RADIUS_CAP_MILES_MAX, Math.max(RADIUS_CAP_MILES_MIN, parsed));
  }

  function initRefs() {
    dlg = queryOne('#storeLocatorFlyout');
    if (!dlg) return false;
    SHOP_DOMAIN = dlg.dataset.shopDomain || '';
    STOREFRONT_TOKEN = dlg.dataset.storefrontToken || '';
    GOOGLE_MAPS_API_KEY = dlg.dataset.googleMapsApiKey || '';
    RADIUS_CAP_MILES = parseRadiusCapMiles(dlg.dataset.radiusCapMiles);

    listEl          = queryOne('[data-store-locator-list]', dlg);
    summaryEl       = queryOne('[data-store-locator-summary]', dlg);
    loadingEl       = queryOne('[data-store-locator-loading]', dlg);
    emptyEl         = queryOne('[data-store-locator-empty]', dlg);
    noResultsEl     = queryOne('[data-store-locator-no-results]', dlg);
    noResultsQuery  = queryOne('[data-store-locator-no-results-query]', dlg);
    footerEl        = queryOne('[data-store-locator-footer]', dlg);
    applyBtn        = queryOne('[data-store-locator-apply]', dlg);
    captureApplyButtonDefaultLabel();
    useLocationBtn  = queryOne('[data-store-locator-use-location]', dlg);
    useLocationText = queryOne('[data-store-locator-use-location-text]', dlg);
    inputEl         = queryOne('#storeLocatorInput', dlg);
    submitBtn       = queryOne('[data-store-locator-submit]', dlg);
    tileTpl         = queryOne('#storeLocatorTileTemplate');
    var radiusMilesEl = queryOne('[data-store-locator-radius-miles]', dlg);
    if (radiusMilesEl) radiusMilesEl.textContent = String(RADIUS_CAP_MILES);
    if (dbgEnabled()) {
      dbg('initRefs', {
        shopDomain: SHOP_DOMAIN,
        storefrontTokenConfigured: !!STOREFRONT_TOKEN,
        googleMapsKeyConfigured: !!GOOGLE_MAPS_API_KEY,
        storefrontApiVersion: STOREFRONT_API_VERSION
      });
    }
    return !!(listEl && tileTpl);
  }

  function show(el)  { if (el) el.hidden = false; }
  function hide(el)  { if (el) el.hidden = true; }

  function syncApplyFooter() {
    if (!footerEl || !applyBtn) return;
    var saved = WSSStore.get();
    var isDifferent = currentSelection &&
      (!saved || String(saved.id) !== String(currentSelection.id));
    if (isDifferent) {
      show(footerEl);
      applyBtn.disabled = false;
      applyBtn.textContent = applyButtonDefaultLabel();
    } else {
      hide(footerEl);
    }
  }

  function setLoading(on) {
    if (on) { show(loadingEl); hide(emptyEl); hide(noResultsEl); hide(summaryEl); listEl.innerHTML = ''; hide(footerEl); }
    else hide(loadingEl);
  }

  function pdpTileStockEnabled(context) {
    return !!(context && context.fromPdp === true && context.variantGid);
  }

  function isMainProductTemplatePage() {
    return !!(document.body && document.body.classList.contains('template-product'));
  }

  function recordMainTemplateProductVariant(detail) {
    if (!isMainProductTemplatePage()) {
      lastMainTemplateProductVariantGid = '';
      lastMainTemplateProductVariantSku = '';
      return;
    }
    var variantId = null;
    var sku = '';
    if (detail && detail.variant && detail.variant.id != null) {
      variantId = detail.variant.id;
      sku = detail.variant.sku || '';
    } else if (detail && detail.variantId != null) {
      variantId = detail.variantId;
      sku = detail.sku || '';
    }
    if (variantId == null) return;
    lastMainTemplateProductVariantGid = 'gid://shopify/ProductVariant/' + variantId;
    lastMainTemplateProductVariantSku = sku || '';
  }

  function seedMainTemplateProductVariantFromDom() {
    if (!isMainProductTemplatePage()) return;
    var gid = '';
    var sku = '';
    var pickup = document.querySelector('[data-pickup-status]');
    if (pickup && pickup.dataset && pickup.dataset.currentVariantGid) {
      gid = pickup.dataset.currentVariantGid;
      sku = pickup.dataset.currentVariantSku || '';
    }
    if (!gid) {
      var root = document.querySelector('.product[data-is-full-product="true"]');
      if (root) {
        var wrap = root.querySelector('.product__variants-wrapper[data-current-variant-id]');
        if (wrap && wrap.dataset.currentVariantId) {
          var wrapperVariantId = String(wrap.dataset.currentVariantId).replace(/\D/g, '');
          if (wrapperVariantId) gid = 'gid://shopify/ProductVariant/' + wrapperVariantId;
        }
        if (!gid) {
          var form = root.querySelector('form[data-product-form]');
          var cid = (form && form.dataset.currentProductId) || root.dataset.currentProductId;
          if (cid != null && cid !== '') {
            var digits = String(cid).replace(/\D/g, '');
            if (digits) gid = 'gid://shopify/ProductVariant/' + digits;
          }
        }
        if (!gid) {
          var idInput = root.querySelector('form[data-product-form] input[name="id"]');
          if (idInput && idInput.value) {
            var inputVariantId = String(idInput.value).replace(/\D/g, '');
            if (inputVariantId) gid = 'gid://shopify/ProductVariant/' + inputVariantId;
          }
        }
      }
    }
    if (!gid) {
      var loneForm = document.querySelector('main form[data-product-form]');
      if (loneForm && loneForm.dataset.currentProductId) {
        var loneFormVariantId = String(loneForm.dataset.currentProductId).replace(/\D/g, '');
        if (loneFormVariantId) gid = 'gid://shopify/ProductVariant/' + loneFormVariantId;
      }
    }
    if (gid) {
      lastMainTemplateProductVariantGid = gid;
      lastMainTemplateProductVariantSku = sku || '';
    }
  }

  // Product page: seed variant from DOM (no event on first paint).
  function scheduleMainTemplateVariantSeedFromDom() {
    if (!isMainProductTemplatePage()) return;
    var run = function () { seedMainTemplateProductVariantFromDom(); };
    run();
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(run);
    }
    window.setTimeout(run, 0);
    window.addEventListener('load', function once() {
      window.removeEventListener('load', once);
      run();
    });
  }

  function headerOpenStoreLocatorContext() {
    if (!isMainProductTemplatePage()) return null;
    if (!lastMainTemplateProductVariantGid) seedMainTemplateProductVariantFromDom();
    if (!lastMainTemplateProductVariantGid) return null;
    return {
      variantGid: lastMainTemplateProductVariantGid,
      sku: lastMainTemplateProductVariantSku || '',
      fromPdp: true
    };
  }

  function openLocator(context) {
    if (!initRefs()) return;
    resetApplyButton();
    __locationsCache = undefined;
    currentContext = pdpTileStockEnabled(context)
      ? { variantGid: context.variantGid, sku: context.sku || '', fromPdp: true }
      : null;
    currentSelection = null;
    dlg.classList.remove('is-closing');
    locatorClosing = false;
    if (!dlg.open) {
      try { dlg.showModal(); } catch (error) { dlg.setAttribute('open', ''); }
    }
    var existing = WSSStore.get();
    if (existing) {
      inputEl.value = existing.zip || '';
      runSearch(existing.zip || (existing.city + ', ' + existing.state), { preselectId: existing.id });
    } else {
      hide(loadingEl); hide(noResultsEl); hide(footerEl); hide(summaryEl);
      show(emptyEl);
      listEl.innerHTML = '';
    }
    if (GOOGLE_MAPS_API_KEY) attachPlacesAutocomplete();
  }
  window.openStoreLocator = openLocator;
  window.openStoreLocatorFromCart = openStoreLocatorFromCart;

  function flushLocatorCloseCallbacks() {
    var callbacks = locatorCloseCallbacks.splice(0);
    callbacks.forEach(function (cb) {
      try { cb(); } catch (error) {}
    });
  }

  function closeLocator(onComplete) {
    if (typeof onComplete === 'function') locatorCloseCallbacks.push(onComplete);
    if (!dlg) {
      flushLocatorCloseCallbacks();
      return;
    }
    if (!dlg.open) {
      currentContext = null;
      flushLocatorCloseCallbacks();
      return;
    }
    if (locatorClosing) return;

    locatorClosing = true;
    currentContext = null;
    dlg.classList.add('is-closing');

    var settled = false;
    function finishClose() {
      if (settled) return;
      settled = true;
      dlg.classList.remove('is-closing');
      locatorClosing = false;
      if (dlg.close) dlg.close();
      else dlg.removeAttribute('open');
      clearLocatorOpenedFromCartUi();
      flushLocatorCloseCallbacks();
    }

    var onAnimEnd = function (event) {
      if (event.target !== dlg || event.animationName !== 'wss-flyout-out') return;
      dlg.removeEventListener('animationend', onAnimEnd);
      finishClose();
    };
    dlg.addEventListener('animationend', onAnimEnd);
    window.setTimeout(finishClose, FLYOUT_ANIM_MS + 40);
  }
  window.closeStoreLocator = closeLocator;

  // ----- Render -----
  function buildTile(item, opts) {
    var store = item.store;
    var distance = item.distance;
    var tile = tileTpl.content.firstElementChild.cloneNode(true);

    var radio = queryOne('[data-store-tile-radio]', tile);
    radio.value = store.id;

    queryOne('[data-store-tile-name]', tile).textContent = store.displayName || store.name;
    var infoLink = queryOne('[data-store-tile-info-link]', tile);
    if (infoLink) {
      infoLink.href = store.pageUrl || STORE_INFO_FALLBACK;
    }

    queryOne('[data-store-tile-street]', tile).textContent =
      store.address1 + (store.address2 ? ' ' + store.address2 : '');
    queryOne('[data-store-tile-city-state-zip]', tile).textContent =
      store.city + (store.state ? ', ' + store.state : '') + (store.zip ? ', ' + store.zip : '');

    var addrLink = queryOne('[data-store-tile-address-link]', tile);
    addrLink.href = 'https://www.google.com/maps/dir/?api=1&destination=' +
      encodeURIComponent(
        (store.address1 || '') + ' ' + (store.city || '') + ', ' +
        (store.state || '') + ' ' + (store.zip || '')
      );

    var distEl = queryOne('[data-store-tile-distance]', tile);
    if (distance != null && !isNaN(distance)) {
      distEl.textContent = formatDistanceMilesDisplay(distance);
      distEl.href = addrLink.href;
    } else {
      distEl.textContent = '';
    }

    var phoneEl = queryOne('[data-store-tile-phone]', tile);
    var phoneFmt = formatPhone(store.phone);
    if (phoneEl) {
      phoneEl.hidden = !phoneFmt;
      phoneEl.classList.toggle('store-tile__phone--empty', !phoneFmt);
      if (phoneFmt) {
        phoneEl.textContent = phoneFmt;
        phoneEl.href = 'tel:' + String(store.phone).replace(/\D/g, '');
      } else {
        phoneEl.textContent = '';
        phoneEl.removeAttribute('href');
      }
    }

    var hoursEl = queryOne('[data-store-tile-hours]', tile);
    var dividerEl = queryOne('.store-tile__divider', tile);
    var expandedEl = queryOne('.store-tile__expanded', tile);

    function applyHoursToTile(hours) {
      var hasHoursData = !!(hours && hours.rows && hours.rows.length);
      if (hoursEl) {
        hoursEl.hidden = !hasHoursData;
        if (hasHoursData) {
          renderStoreTileHoursList(hoursEl, hours);
        } else {
          hoursEl.innerHTML = '';
        }
      }
      if (dividerEl) dividerEl.hidden = !hasHoursData;
      var hasExpandedContent = !!(phoneFmt || hasHoursData);
      if (expandedEl) {
        expandedEl.hidden = !hasExpandedContent;
        expandedEl.classList.toggle('store-tile__expanded--empty', !hasExpandedContent);
      }
    }

    applyHoursToTile(formatHours(store.hoursRaw));

    if (currentContext && currentContext.fromPdp && currentContext.variantGid) {
      var entry = availabilityEntryForStore(store);
      var resolved = pickupAvailabilityStatus(entry, 1);
      tile.dataset.stockStatus = resolved.status;
      queryOne('[data-store-tile-status]', tile).hidden = false;
      queryOne('[data-store-tile-status-text]', tile).textContent = statusLabel(resolved.status);
    } else {
      var statusRow = queryOne('[data-store-tile-status]', tile);
      if (statusRow) {
        statusRow.hidden = true;
        delete tile.dataset.stockStatus;
        var statusTxt = queryOne('[data-store-tile-status-text]', tile);
        if (statusTxt) statusTxt.textContent = '';
      }
    }

    if (opts && opts.isMyStore) {
      tile.classList.add('is-my-store');
      queryOne('[data-badge-shopping]', tile).dataset.visible = 'true';
    }

    if (opts && opts.isInitiallySelected) {
      tile.classList.add('is-selected');
      radio.checked = true;
      currentSelection = store;
    }

    radio.addEventListener('change', function () {
      queryAll('.store-tile', listEl).forEach(function (tileEl) {
        tileEl.classList.remove('is-selected');
        var tileRadio = queryOne('[data-store-tile-radio]', tileEl);
        if (tileRadio) tileRadio.checked = false;
      });
      tile.classList.add('is-selected');
      radio.checked = true;
      currentSelection = store;
      syncApplyFooter();
    });

    tile.addEventListener('click', function (event) {
      if (event.target.closest('a')) return;
      if (event.target.closest('[data-store-tile-radio]')) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change'));
    });

    return tile;
  }

  function renderResults(results, queryLabel) {
    hide(loadingEl);
    listEl.innerHTML = '';
    var existing = WSSStore.get();
    if (!results.length) {
      hide(emptyEl); hide(footerEl); hide(summaryEl);
      noResultsQuery.textContent = queryLabel === 'your location'
        ? queryLabel
        : (stripCountryFromQueryLabel(queryLabel) || queryLabel || 'your area');
      show(noResultsEl);
      return;
    }
    hide(emptyEl); hide(noResultsEl);
    var countLabel = results.length + (results.length === 1 ? ' Store' : ' Stores');
    var milesLabel = RADIUS_CAP_MILES + ' miles';
    var queryPart = queryLabel
      ? ' of <strong>' + escapeHtml(queryLabel) + '</strong>.'
      : '.';
    summaryEl.innerHTML =
      '<strong>' + escapeHtml(countLabel) + '</strong> found within ' +
      escapeHtml(milesLabel) + queryPart;
    show(summaryEl);

    results.forEach(function (item, idx) {
      var isMyStore = !!(existing && String(existing.id) === String(item.store.id));
      var tile = buildTile(item, {
        isMyStore: isMyStore,
        isInitiallySelected: isMyStore || (!existing && idx === 0)
      });
      listEl.appendChild(tile);
    });

    syncApplyFooter();
  }

  function runSearch(rawQuery, opts) {
    opts = opts || {};
    var query = (rawQuery || '').trim();
    if (!query && !opts.presetGeo) {
      hide(loadingEl);
      hide(noResultsEl); hide(footerEl); hide(summaryEl);
      show(emptyEl);
      listEl.innerHTML = '';
      return;
    }
    setLoading(true);
    var geoPromise = opts.presetGeo
      ? Promise.resolve(opts.presetGeo)
      : geocode(query);
    var preloadMaps = GOOGLE_MAPS_API_KEY
      ? ensureGoogleMapsLoaded().catch(function () {})
      : Promise.resolve();
    Promise.all([
      geoPromise,
      fetchLocations(),
      preloadMaps
    ]).then(function (searchBundle) {
      var geo = searchBundle[0];
      var locations = searchBundle[1] || [];
      if (dbgEnabled()) {
        dbg('runSearch: geocode result', geo, '| normalized locations:', locations.length);
      }
      var availabilityPromise = (currentContext && currentContext.fromPdp && currentContext.variantGid)
        ? fetchAvailability(currentContext.variantGid)
        : Promise.resolve({});
      return availabilityPromise.then(function (availability) {
        availabilityMap = availability || {};
        var searchZip5 = normZip5(query);

        if (!geo && searchZip5) {
          setLoading(false);
          var zipOnly = buildZipOnlyResults(locations, searchZip5);
          if (dbgEnabled()) dbg('runSearch: ZIP-only path (no geo)', { searchZip5: searchZip5, zipOnlyCount: zipOnly.length });
          if (dbgEnabled() && zipOnly.length === 0 && locations.length) {
            dbg('ZIP-only path: no store address.zip matches', searchZip5, '— check Shopify Location ZIP fields');
            debugExplainEmptyResults(locations, null, searchZip5);
          }
          currentQueryLabel = searchZip5;
          currentResults = zipOnly;
          renderResults(zipOnly, currentQueryLabel);
          return;
        }

        if (!geo) {
          setLoading(false);
          if (dbgEnabled()) dbg('runSearch: no geocode result and no ZIP-only fallback for', query);
          renderResults([], query);
          return;
        }
        currentQueryLabel = stripCountryFromQueryLabel(geo.label) || geo.label || query;

        return enrichMissingCoordinates(locations).then(function (enriched) {
          var withDistance = buildResultsWithDistance(enriched, geo, searchZip5);

          if (dbgEnabled() && withDistance.length === 0) {
            debugExplainEmptyResults(enriched, geo, searchZip5);
          }

          if (opts && opts.preselectId) {
            var inSet = withDistance.some(function (resultItem) { return String(resultItem.store.id) === String(opts.preselectId); });
            if (!inSet) {
              var pinned = enriched.find(function (location) { return String(location.id) === String(opts.preselectId); });
              if (pinned) {
                var pd = null;
                if (searchZip5 && normZip5(pinned.zip) === searchZip5) pd = 0;
                else if (hasValidCoords(pinned) && geo) pd = distanceMiles(geo.lat, geo.lng, pinned.lat, pinned.lng);
                if (pd !== null && pd <= RADIUS_CAP_MILES) {
                  withDistance.unshift({ store: pinned, distance: pd });
                }
              }
            }
          }

          currentResults = withDistance;
          setLoading(false);
          if (dbgEnabled()) {
            dbg('runSearch: final results', {
              searchZip5: searchZip5,
              resultCount: withDistance.length,
              rows: withDistance.map(function (resultRow) {
                return {
                  id: resultRow.store.id,
                  name: resultRow.store.name,
                  displayName: resultRow.store.displayName,
                  zip: resultRow.store.zip,
                  miles: resultRow.distance,
                  hasCoords: hasValidCoords(resultRow.store),
                  pageUrl: resultRow.store.pageUrl,
                  placeId: resultRow.store.placeId
                };
              })
            });
          }
          renderResults(withDistance, currentQueryLabel);
        }).catch(function (err) {
          dbg('enrichMissingCoordinates failed', err);
          var fallback = buildResultsWithDistance(locations, geo, searchZip5);
          if (dbgEnabled() && fallback.length === 0) {
            debugExplainEmptyResults(locations, geo, searchZip5);
          }
          currentResults = fallback;
          setLoading(false);
          renderResults(fallback, currentQueryLabel);
        });
      });
    }).catch(function (err) {
      dbg('search failed', err);
      setLoading(false);
      renderResults([], query);
    });
  }

  function sessionFlagGet(key) {
    try { return window.sessionStorage.getItem(key) === '1'; } catch (error) { return false; }
  }
  function sessionFlagSet(key) {
    try { window.sessionStorage.setItem(key, '1'); } catch (error) {}
  }

  function isAutoGeoDefaultEnabled() {
    if (!dlg) return true;
    var flagValue = String(dlg.dataset.autoGeoDefault || '').toLowerCase();
    if (flagValue === 'false' || flagValue === '0' || flagValue === 'no') return false;
    return true;
  }

  function storesNearUserGeo(lat, lng) {
    var userGeo = { lat: lat, lng: lng, label: 'your location' };
    var radiusOpts = deviceGeoRadiusOpts();
    var preloadMaps = GOOGLE_MAPS_API_KEY
      ? ensureGoogleMapsLoaded().catch(function () {})
      : Promise.resolve();
    return reverseGeocodeToZip5(lat, lng).then(function (userZip5) {
      var zipCentroidPromise = userZip5 ? geocode(userZip5) : Promise.resolve(null);
      return Promise.all([fetchLocations(), preloadMaps, zipCentroidPromise]).then(function (geoBundle) {
        var locations = geoBundle[0] || [];
        var zipCentroid = geoBundle[2] || null;
        if (dbgEnabled()) {
          dbg('storesNearUserGeo', {
            userZip5: userZip5,
            zipCentroid: zipCentroid,
            maxRadiusMiles: radiusOpts.maxRadiusMiles
          });
        }
        function buildMerged(enriched) {
          var fromGps = buildResultsWithDistance(enriched, userGeo, userZip5, radiusOpts);
          if (!zipCentroid) return fromGps;
          var fromZipCentroid = buildResultsWithDistance(
            enriched,
            zipCentroid,
            userZip5,
            { maxRadiusMiles: RADIUS_CAP_MILES }
          );
          if (dbgEnabled()) {
            dbg('storesNearUserGeo merge', {
              fromGps: fromGps.length,
              fromZipCentroid: fromZipCentroid.length
            });
          }
          return mergeDistanceResults(fromGps, fromZipCentroid);
        }
        return enrichMissingCoordinates(locations).then(function (enriched) {
          var results = buildMerged(enriched);
          if (dbgEnabled() && results.length === 0) {
            debugExplainEmptyResults(enriched, userGeo, userZip5, radiusOpts);
            if (zipCentroid) {
              dbg('ZERO results — ZIP centroid diagnostic (' + RADIUS_CAP_MILES + 'mi):');
              debugExplainEmptyResults(enriched, zipCentroid, userZip5, { maxRadiusMiles: RADIUS_CAP_MILES });
            }
          }
          return results;
        }).catch(function (err) {
          dbg('storesNearUserGeo enrich failed', err);
          var fallback = buildMerged(locations);
          if (dbgEnabled() && fallback.length === 0) {
            debugExplainEmptyResults(locations, userGeo, userZip5, radiusOpts);
          }
          return fallback;
        });
      });
    });
  }

  function nearestStoreFromUserGeo(lat, lng) {
    return storesNearUserGeo(lat, lng).then(function (withDistance) {
      return withDistance.length ? withDistance[0].store : null;
    });
  }

  // First visit: save nearest store from geolocation (no flyout).
  function maybeAutoSelectDefaultStore() {
    if (!initRefs()) return;
    if (!isAutoGeoDefaultEnabled()) {
      if (dbgEnabled()) dbg('autoGeo: disabled via theme setting');
      return;
    }
    if (WSSStore.get()) return;
    if (sessionFlagGet(SS_GEO_AUTO_DENIED)) {
      if (dbgEnabled()) dbg('autoGeo: skip — denied this session');
      return;
    }
    if (sessionFlagGet(SS_GEO_AUTO_EMPTY)) {
      if (dbgEnabled()) dbg('autoGeo: skip — no nearby store this session');
      return;
    }
    if (!STOREFRONT_TOKEN) {
      if (dbgEnabled()) dbg('autoGeo: skip — no Storefront token');
      return;
    }
    if (!('geolocation' in navigator)) {
      if (dbgEnabled()) dbg('autoGeo: skip — geolocation unavailable');
      return;
    }

    navigator.geolocation.getCurrentPosition(function (pos) {
      nearestStoreFromUserGeo(pos.coords.latitude, pos.coords.longitude)
        .then(function (store) {
          if (!store) {
            sessionFlagSet(SS_GEO_AUTO_EMPTY);
            if (dbgEnabled()) {
              dbg('autoGeo: no store within', RADIUS_CAP_MILES + GEO_RADIUS_SLACK_MILES, 'miles (device geo cap)');
            }
            return;
          }
          if (dbgEnabled()) {
            dbg('autoGeo: saving default store', {
              id: store.id,
              name: store.displayName || store.name
            });
          }
          return WSSStore.set(store);
        })
        .catch(function (err) {
          if (dbgEnabled()) dbg('autoGeo: lookup failed', err);
        });
    }, function (err) {
      sessionFlagSet(SS_GEO_AUTO_DENIED);
      if (dbgEnabled()) dbg('autoGeo: permission denied or error', err && err.code);
    }, { maximumAge: 5 * 60 * 1000, timeout: 10000 });
  }

  // ----- Use my location -----
  function useMyLocation() {
    if (!('geolocation' in navigator)) {
      useLocationBtn.dataset.denied = 'true';
      useLocationText.textContent = 'Geolocation permission denied';
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(function (pos) {
      var lat = pos.coords.latitude, lng = pos.coords.longitude;
      if (dbgEnabled()) {
        dbg('useMyLocation: device geo', {
          lat: lat,
          lng: lng,
          accuracyM: pos.coords.accuracy
        });
      }
      var availPromise = currentContext && currentContext.fromPdp && currentContext.variantGid
        ? fetchAvailability(currentContext.variantGid)
        : Promise.resolve({});
      Promise.all([storesNearUserGeo(lat, lng), availPromise]).then(function (locationBundle) {
        var withDistance = locationBundle[0] || [];
        availabilityMap = locationBundle[1] || {};
        if (dbgEnabled()) {
          dbg('useMyLocation: results', { count: withDistance.length });
        }
        currentResults = withDistance;
        currentQueryLabel = 'your location';
        setLoading(false);
        renderResults(withDistance, currentQueryLabel);
      }).catch(function (err) {
        dbg('useMyLocation fetch failed', err);
        setLoading(false);
        renderResults([], 'your location');
      });
    }, function () {
      useLocationBtn.dataset.denied = 'true';
      useLocationText.textContent = 'Geolocation permission denied';
      setLoading(false);
    }, { maximumAge: 5 * 60 * 1000, timeout: 10000 });
  }

  // ----- Apply -----
  function captureApplyButtonDefaultLabel() {
    if (!applyBtn) return;
    var trimmed = (applyBtn.textContent || '').trim();
    if (!trimmed || trimmed === APPLY_BTN_LOADING_LABEL) return;
    applyBtnDefaultLabel = trimmed;
  }

  function applyButtonDefaultLabel() {
    var label = applyBtnDefaultLabel || APPLY_BTN_DEFAULT_LABEL;
    return label === APPLY_BTN_LOADING_LABEL ? APPLY_BTN_DEFAULT_LABEL : label;
  }

  function resetApplyButton() {
    if (!dlg) dlg = queryOne('#storeLocatorFlyout');
    var btn = applyBtn || (dlg && queryOne('[data-store-locator-apply]', dlg));
    if (!btn) return;
    applyBtn = btn;
    btn.disabled = false;
    btn.textContent = applyButtonDefaultLabel();
  }

  function reopenQuickCartAfterStoreApply() {
    requestAnimationFrame(function () {
      document.dispatchEvent(new CustomEvent('quick-cart:open'));
    });
  }

  function finishStoreApply(opts) {
    opts = opts || {};
    resetApplyButton();
    hide(footerEl);
    closeLocator(function () {
      if (opts.reopenQuickCart) reopenQuickCartAfterStoreApply();
    });
  }

  function applySelection() {
    if (!currentSelection) return;
    if (!applyBtn && !initRefs()) return;
    applyBtn.disabled = true;
    applyBtn.textContent = APPLY_BTN_LOADING_LABEL;
    var reopenQuickCart = wasStoreLocatorOpenedFromQuickCart();
    // Cookie/localStorage + wss:store-changed are synchronous; cart sync runs in background.
    WSSStore.set(currentSelection);
    clearLocatorOpenedFromCartUi();
    finishStoreApply({ reopenQuickCart: reopenQuickCart });
  }

  function bindStoreLocatorListeners() {
    window.addEventListener('wss:store-changed', hydrateTriggers);
    window.addEventListener('storage', function (event) {
      if (event.key === LS_KEY) hydrateTriggers();
    });

    window.addEventListener('wss:store-changed', onStoreChangedForCartToggle);
    window.addEventListener('wss:store-changed', hydrateCartPickup);

    document.addEventListener('cart:updated', onCartUpdatedForPickupPref);
    document.addEventListener('cart:updated', function (evt) {
      var cart = evt && evt.detail && evt.detail.cart;
      if (cart) {
        hydrateThemeStoreStateFromCart(cart);
      } else {
        loadCartFulfillmentMethod().then(function () {
          hydrateTriggers();
          renderCartPickupHeaders();
          hydrateCartPickup();
        });
        return;
      }
      renderCartPickupToggles();
      hydrateCartPickup();
    });

    document.addEventListener('quick-cart:open', function () {
      hydrateCartPickup();
    });

    fetchAjaxCart().then(syncCartStateFromServer).catch(function () {});

    bindCartCheckoutPreferenceSync();
    bindCartPickupUnavailableModal();
    bindCartPickupNavigationRecovery();
    bindCartPickupQuantityListener();
    window.addEventListener('pageshow', function (event) {
      var restoredFromModalCheckout = restoreCartPageAfterNavigation({ persisted: event.persisted });
      if (event.persisted || document.querySelector('.cart.page')) {
        var syncCartState = function () {
          onStoreStatePageShow({ skipCheckoutPush: true });
        };
        if (restoredFromModalCheckout && window.matchMedia('(max-width: 959px)').matches) {
          requestAnimationFrame(syncCartState);
        } else {
          syncCartState();
        }
      }
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') onStoreStatePageShowAlways();
    });

    var cartPickupLayoutMq = window.matchMedia('(max-width: 959px)');
    var cartPickupLayoutListener = function () {
      if (typeof window.hydrateCartPickup === 'function') window.hydrateCartPickup();
    };
    if (cartPickupLayoutMq.addEventListener) {
      cartPickupLayoutMq.addEventListener('change', cartPickupLayoutListener);
    } else if (cartPickupLayoutMq.addListener) {
      cartPickupLayoutMq.addListener(cartPickupLayoutListener);
    }
  }

  function bindFlyout() {
    if (!initRefs()) return;

    submitBtn.addEventListener('click', function () { runSearch(inputEl.value); });
    inputEl.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') { event.preventDefault(); runSearch(inputEl.value); }
    });
    useLocationBtn.addEventListener('click', useMyLocation);
    applyBtn.addEventListener('click', applySelection);

    dlg.addEventListener('click', function (event) {
      if (event.target === dlg) {
        event.preventDefault();
        closeLocator();
      }
    });
    dlg.addEventListener('click', function (event) {
      if (event.target.closest('[data-store-locator-close], .wss-modal__close')) {
        event.preventDefault();
        event.stopPropagation();
        closeLocator();
      }
    });
    dlg.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeLocator();
    });
    dlg.addEventListener('close', function () {
      resetApplyButton();
      currentContext = null;
    });

    function refreshFlyoutAvailabilityFromVariantDetail(detail) {
      if (!dlg || !dlg.open || !currentContext || !currentContext.fromPdp || !currentContext.variantGid) return;
      var variantId = null;
      var sku = '';
      if (detail && detail.variant && detail.variant.id != null) {
        variantId = detail.variant.id;
        sku = detail.variant.sku || '';
      } else if (detail && detail.variantId != null) {
        variantId = detail.variantId;
        sku = detail.sku || '';
      }
      if (variantId == null) return;
      currentContext.variantGid = 'gid://shopify/ProductVariant/' + variantId;
      if (sku) currentContext.sku = sku;
      fetchAvailability(currentContext.variantGid).then(function (map) {
        availabilityMap = map || {};
        if (currentResults && currentResults.length) {
          renderResults(currentResults, currentQueryLabel);
        }
      });
    }
    document.addEventListener('product:variant-change', function (event) {
      recordMainTemplateProductVariant(event.detail);
      refreshFlyoutAvailabilityFromVariantDetail(event.detail);
    });
    document.addEventListener('variant:change', function (event) {
      recordMainTemplateProductVariant(event.detail);
      refreshFlyoutAvailabilityFromVariantDetail(event.detail);
    });

    scheduleMainTemplateVariantSeedFromDom();

    // Clear a pending pickup-enable if the locator closes without a store selection.
    dlg.addEventListener('close', function () {
      if (pendingEnablePickup) {
        pendingEnablePickup = false;
        renderCartPickupToggles();
      }
    });
  }

  function hydrateTriggers() {
    var store = WSSStore.get();
    queryAll('[data-store-locator-trigger]').forEach(function (trigger) {
      var secondary = trigger.querySelector('[data-find-store-secondary]');
      var primary = trigger.querySelector('[data-find-store-primary]');
      var primaryNo = trigger.getAttribute('data-primary-no-store');
      var primaryYes = trigger.getAttribute('data-primary-with-store');
      var hasStore = !!(store && (store.displayName || store.name));
      if (hasStore) {
        trigger.dataset.noStore = 'false';
        if (secondary) {
          secondary.textContent = store.displayName || store.name;
        }
      } else {
        trigger.dataset.noStore = 'true';
        if (secondary) {
          secondary.textContent = secondary.getAttribute('data-placeholder') || '';
        }
      }
      if (primary && primaryNo != null && primaryYes != null) {
        primary.textContent = hasStore ? primaryYes : primaryNo;
      }
      if (!trigger.__wssBound) {
        trigger.__wssBound = true;
        function openFromTrigger(e) {
          event.preventDefault();
          var context = headerOpenStoreLocatorContext();
          if (document.querySelector('.cart.page') || document.querySelector('.quick-cart__wrapper.active')) {
            openStoreLocatorFromCart(context);
          } else {
            openLocator(context);
          }
        }
        trigger.addEventListener('click', openFromTrigger);
        if (trigger.getAttribute('role') === 'button') {
          trigger.addEventListener('keydown', function (event) {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openFromTrigger(e);
            }
          });
        }
      }
    });
  }

  // Hide sticky header when scrolling down (mobile store strip is in header).
  function bindScrollDirection() {
    var header = document.querySelector('.header[data-is-sticky="true"]');
    if (!header) return;
    var lastScrollY = window.scrollY || 0;
    var threshold = 80;
    var ticking = false;

    function update() {
      ticking = false;
      var scrollY = window.scrollY || 0;
      if (scrollY < threshold) {
        header.classList.remove('header--scrolled-down');
      } else if (scrollY > lastScrollY) {
        header.classList.add('header--scrolled-down');
      } else if (scrollY < lastScrollY) {
        header.classList.remove('header--scrolled-down');
      }
      lastScrollY = scrollY;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
  }

  // Paint header/cart pickup from cookie before waiting on cart.js.
  seedCartFulfillmentMethodFromDom();
  paintSavedStoreUi();

  ready(function () {
    bindStoreLocatorListeners();
    bindFlyout();
    bindScrollDirection();
    setTimeout(maybeAutoSelectDefaultStore, 400);
  });

  // DevTools: __WSS_STORE_LOCATOR_DEBUG_INFO(true) toggles verbose logs; call with no args for status.
  window.__WSS_STORE_LOCATOR_DEBUG_INFO = function (enableVerbose) {
    initRefs();
    if (enableVerbose === true || enableVerbose === 1 || enableVerbose === '1') {
      window.__WSS_STORE_LOCATOR_DEBUG = true;
    } else if (enableVerbose === false || enableVerbose === 0 || enableVerbose === '0') {
      window.__WSS_STORE_LOCATOR_DEBUG = false;
    } else if (enableVerbose && typeof enableVerbose === 'object' && 'verbose' in enableVerbose) {
      window.__WSS_STORE_LOCATOR_DEBUG = !!enableVerbose.verbose;
    }
    var on = dbgEnabled();
    if (on) {
      dbg('__WSS_STORE_LOCATOR_DEBUG_INFO: verbose logging is ON for this tab (reload clears window.__WSS_STORE_LOCATOR_DEBUG unless URL/localStorage debug is set).');
    }
    return {
      dbgEnabled: on,
      shopDomain: SHOP_DOMAIN,
      storefrontTokenConfigured: !!STOREFRONT_TOKEN,
      googleMapsKeyConfigured: !!GOOGLE_MAPS_API_KEY,
      storefrontApiVersion: STOREFRONT_API_VERSION,
      cachedLocationCount: __locationsCache === undefined ? null : __locationsCache.length,
      toggleVerbose: 'Call __WSS_STORE_LOCATOR_DEBUG_INFO(true) to log searches in this tab; __WSS_STORE_LOCATOR_DEBUG_INFO(false) to turn off (unless URL ?store_locator_debug=1 or localStorage is set).',
      clearCache: 'Cache resets each time the store locator flyout opens; full page reload if you change the Storefront token.'
    };
  };
})();
